// src/lib/calculator/peptide-data.ts

import { getContentFallbackChain, type Locale } from "@/config/locale";
import { isFiveProductLaunchLive } from "@/config/five-product-launch";
import { getFallbackTextLanguage } from "@/lib/seo/fallback-text-language";
import type { PeptideCalculatorMode, PeptideData, PeptideLocalizedText, StudyReference } from "./types";

/**
 * Locale of the content chain (es-mx: its own entry, then es) whose entry a localized text uses,
 * or undefined when no chain locale has one; every non-regional locale reads exactly its own entry.
 */
export function resolvePeptideTextLocale(
	text: PeptideLocalizedText | undefined,
	locale: Locale,
): Locale | undefined {
	return text ? getContentFallbackChain(locale).find((candidate) => text[candidate] != null) : undefined;
}

/** Text of the locale's content chain; callers add their own last fallback (German or English). */
export function pickPeptideText(text: PeptideLocalizedText | undefined, locale: Locale): string | undefined {
	const source = resolvePeptideTextLocale(text, locale);
	return source ? text?.[source] : undefined;
}

export function getPeptideName(peptide: PeptideData, locale: Locale): string {
	return pickPeptideText(peptide.name, locale) ?? peptide.name.de;
}

export function getPeptideCategory(peptide: PeptideData, locale: Locale): string {
	return pickPeptideText(peptide.category, locale) ?? peptide.category.de;
}

export function getPeptideHalfLifeSource(peptide: PeptideData, locale: Locale): string | undefined {
	return pickPeptideText(peptide.halfLifeSourceLocalized, locale) ?? peptide.halfLifeSource;
}

/**
 * `lang` of the study note `getPeptideHalfLifeSource` returns, for every surface that shows it:
 * "es" when es-mx shows the es note (spec 5.5), undefined for the route's own note, the
 * unlocalized legacy note and on every non-regional locale.
 */
export function getPeptideHalfLifeSourceLanguage(peptide: PeptideData, locale: Locale): string | undefined {
	return getFallbackTextLanguage(locale, resolvePeptideTextLocale(peptide.halfLifeSourceLocalized, locale));
}

/** Calculator behavior defaults to the established dose workflow. */
export function getPeptideCalculatorMode(peptide: PeptideData | undefined): PeptideCalculatorMode {
	return peptide?.calculatorMode ?? "dose";
}

/** Hub calculator only — no spoke page, sitemap entry, or SEO article. */
export const CUSTOM_PEPTIDE_SLUG = "custom" as const;

export const PEPTIDE_DATA: PeptideData[] = [
	{
		slug: CUSTOM_PEPTIDE_SLUG,
		name: {
			de: "Eigenes Peptid",
			en: "Custom peptide",
			it: "Peptide personalizzato",
			es: "Péptido personalizado",
			fr: "Peptide personnalisé",
			nl: "Aangepast peptide",
			pt: "Péptido personalizado",
			"pt-br": "Peptídeo personalizado",
			pl: "Własny peptyd",
			sv: "Eget peptid",
			da: "Eget peptid",
			el: "Δικό σας πεπτίδιο",
			cs: "Vlastní peptid",
			fi: "Oma peptidi",
			ro: "Peptidă personalizată",
			hu: "Saját peptid",
			sk: "Vlastný peptid",
			tr: "Özel peptit",
			nb: "Eget peptid",
			bg: "Собствен пептид",
			hr: "Prilagođeni peptid",
			sl: "Peptid po meri",
			sr: "Prilagođeni peptid",
			bs: "Prilagođeni peptid",
			mk: "Сопствен пептид",
			lt: "Pasirinktinis peptidas",
			lv: "Pielāgots peptīds",
			et: "Kohandatud peptiid",
		},
		category: {
			de: "Sonstiges",
			en: "Other",
			it: "Altro",
			es: "Otros",
			fr: "Autre",
			nl: "Overig",
			pt: "Outro",
			"pt-br": "Outro",
			pl: "Inne",
			sv: "Övrigt",
			da: "Andet",
			el: "Άλλο",
			cs: "Jiné",
			fi: "Muu",
			ro: "Altele",
			hu: "Egyéb",
			sk: "Iné",
			tr: "Diğer",
			nb: "Annet",
			bg: "Други",
			hr: "Ostalo",
			sl: "Drugo",
			sr: "Ostalo",
			bs: "Ostalo",
			mk: "Друго",
			lt: "Kita",
			lv: "Cits",
			et: "Muu",
		},
		defaultVialSizes: [2, 5, 10, 15, 20],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.5,
		halfLife: "—",
		frequency: "—",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: false,
		relatedPeptides: [],
	},
	{
		slug: "vip",
		name: {
			en: "VIP",
			de: "VIP",
			it: "VIP",
			es: "VIP",
			fr: "VIP",
			nl: "VIP",
			pt: "VIP",
			"pt-br": "VIP",
			pl: "VIP",
			sv: "VIP",
			da: "VIP",
			el: "VIP",
			cs: "VIP",
			fi: "VIP",
			ro: "VIP",
			hu: "VIP",
			sk: "VIP",
			tr: "VIP",
			nb: "VIP",
			bg: "VIP",
			hr: "VIP",
			sl: "VIP",
			sr: "VIP",
			bs: "VIP",
			mk: "VIP",
			lt: "VIP",
			lv: "VIP",
			et: "VIP",
		},
		category: {
			en: "Neuropeptide Research",
			de: "Neuropeptid-Forschung",
			it: "Ricerca sui neuropeptidi",
			es: "Investigación de neuropéptidos",
			fr: "Recherche sur les neuropeptides",
			nl: "Neuropeptidenonderzoek",
			pt: "Investigação de neuropéptidos",
			"pt-br": "Pesquisa de neuropeptídeos",
			pl: "Badania neuropeptydów",
			sv: "Neuropeptidforskning",
			da: "Neuropeptidforskning",
			el: "Έρευνα νευροπεπτιδίων",
			cs: "Výzkum neuropeptidů",
			fi: "Neuropeptiditutkimus",
			ro: "Cercetare neuropeptidică",
			hu: "Neuropeptid-kutatás",
			sk: "Výskum neuropeptidov",
			tr: "Nöropeptit araştırması",
			nb: "Nevropeptidforskning",
			bg: "Изследване на невропептиди",
			hr: "Istraživanje neuropeptida",
			sl: "Raziskovanje nevropeptidov",
			sr: "Istraživanje neuropeptida",
			bs: "Istraživanje neuropeptida",
			mk: "Истражување на невропептиди",
			lt: "Nevropeptidų tyrimai",
			lv: "Neiropetīdu pētniecība",
			et: "Neuropeptiidide uurimine",
		},
		calculatorMode: "research-dilution",
		defaultVialSizes: [10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.1,
		halfLife: "~1 minute (intravenous disappearance)",
		halfLifeHours: 1 / 60,
		halfLifeSource:
			"Domschke et al., Gut 1978: plasma disappearance after intravenous infusion in four healthy volunteers, average half-time about one minute. This small IV study is not an intracavernous Invicorp pharmacokinetic value or a general reference for VIP research material.",
		halfLifeSourceLocalized: {
			en: "Domschke et al., Gut 1978: plasma disappearance after intravenous infusion in four healthy volunteers, average half-time about one minute. This small IV study is not an intracavernous Invicorp pharmacokinetic value or a general reference for VIP research material.",
			de: "Domschke et al., Gut 1978: Plasmaabfall nach IV-Infusion bei vier gesunden Freiwilligen, mittlere Verschwinde-Halbwertszeit etwa 1 min; kleine IV-Studienreferenz, keine PK-Angabe zur intrakavernösen Invicorp-Kombination.",
			it: "Domschke et al., Gut 1978: scomparsa plasmatica dopo infusione endovenosa in quattro volontari sani, circa un minuto. Piccolo riferimento sperimentale IV, non un valore farmacocinetico dell'Invicorp intracavernoso.",
			es: "Domschke et al., Gut 1978: desaparición plasmática tras infusión intravenosa en cuatro voluntarios sanos, aproximadamente un minuto. Referencia de un estudio pequeño intravenoso, no farmacocinética intracavernosa de Invicorp.",
			fr: "Domschke et al., Gut 1978 : disparition plasmatique après perfusion intraveineuse chez quatre volontaires sains, environ une minute ; petite étude, sans valeur pharmacocinétique de l'Invicorp intracaverneux.",
			nl: "Domschke et al., Gut 1978: verdwijnen uit plasma na intraveneuze infusie bij vier gezonde vrijwilligers, ongeveer één minuut; kleine IV-studiereferentie, geen Invicorp-waarde voor toediening in het zwellichaam",
			pt: "Domschke et al., Gut 1978: desaparecimento plasmático após perfusão intravenosa em quatro voluntários saudáveis, cerca de um minuto. Pequena referência de estudo IV, não farmacocinética do Invicorp intracavernoso.",
			"pt-br":
				"Domschke et al., Gut 1978: desaparecimento plasmático após infusão intravenosa em quatro voluntários saudáveis, aproximadamente um minuto. Referência de um pequeno estudo intravenoso, não um parâmetro farmacocinético intracavernoso de Invicorp.",
			pl: "Domschke et al., Gut 1978: zanik w osoczu po infuzji dożylnej u czterech zdrowych ochotników, około jednej minuty. Mała referencja dożylna, nie farmakokinetyka Invicorp podawanego do ciał jamistych.",
			sv: "Domschke et al., Gut 1978: efter avslutad intravenös infusion minskade plasmanivåerna med en genomsnittlig halveringstid på omkring en minut hos fyra friska försökspersoner. Liten intravenös studiereferens, inte farmakokinetik för intrakavernöst Invicorp.",
			da: "Domschke et al., Gut 1978: omkring ét minuts forsvindingshalveringstid i plasma efter intravenøs infusion hos fire raske frivillige. Lille intravenøst studie; ikke en farmakokinetisk værdi for intrakavernøst Invicorp.",
			el: "Domschke et al., Gut 1978: χρόνος εξαφάνισης VIP από πλάσμα περίπου ένα λεπτό μετά από ενδοφλέβια έγχυση σε τέσσερις υγιείς εθελοντές. Μικρή ενδοφλέβια αναφορά, όχι φαρμακοκινητική του ενδοσηραγγώδους Invicorp.",
			cs: "Domschke et al., Gut 1978: mizení z plazmy po nitrožilní infuzi u čtyř zdravých dobrovolníků, přibližně jedna minuta. Malá IV studijní reference, nikoli farmakokinetický údaj intrakavernózního přípravku Invicorp.",
			fi: "Domschke et al., Gut 1978: laskimoinfuusion jälkeinen plasmapitoisuuden häviäminen, neljä tervettä vapaaehtoista, keskimäärin noin minuutti. Pienen laskimonsisäisen tutkimuksen viite, ei paisuvaiskudokseen annetun Invicorpin farmakokinetiikka.",
			ro: "Domschke et al., Gut 1978: dispariție plasmatică după perfuzie intravenoasă la patru voluntari sănătoși, circa un minut; referință de studiu mică, fără a reprezenta farmacocinetica Invicorp intracavernos.",
			hu: "Domschke et al., Gut 1978: négy egészséges önkéntes intravénás infúziója után körülbelül egyperces plazmabeli eltűnési felezési idő. Kis IV-vizsgálati referencia, nem az intrakavernózus Invicorp kombináció PK-értéke.",
			sk: "Domschke et al., Gut 1978: vymiznutie VIP z plazmy po intravenóznej infúzii u štyroch zdravých dobrovoľníkov, približne jedna minúta; malá študijná referencia, nie farmakokinetika intrakavernózneho Invicorpu.",
			tr: "Domschke et al., Gut 1978: dört sağlıklı gönüllüde intravenöz infüzyon sonrası plazmadan kaybolma yarı süresi yaklaşık bir dakika. Küçük IV çalışma referansıdır; kavernöz Invicorp kombinasyonunun farmakokinetik değeri değildir.",
			nb: "Domschke et al., Gut 1978: intravenøs plasmaforsvinning hos fire friske frivillige, omtrent ett minutt. Liten IV-studiereferanse, ikke intrakavernøs Invicorp-kinetikk.",
			bg: "Domschke et al., Gut 1978: плазмено изчезване след интравенозна инфузия при четирима здрави доброволци, около една минута. Малка интравенозна изследователска референция, не фармакокинетика на интракавернозния Invicorp.",
			hr: "Domschke et al., Gut 1978: nestajanje iz plazme nakon intravenske infuzije u četiri zdrava dobrovoljca, približno jedna minuta; mala studijska referenca, ne intrakavernozna kinetika Invicorpa.",
			sl: "Domschke et al., Gut 1978: izginjanje iz plazme po intravenski infuziji pri štirih zdravih prostovoljcih, povprečni razpolovni čas približno ena minuta; majhna študijska referenca, ne intrakavernozna kinetika zdravila Invicorp.",
			sr: "Domschke et al., Gut 1978: nestajanje iz plazme nakon intravenske infuzije kod četiri zdrava dobrovoljca, prosečno poluvreme oko jedan minut; mala studijska referenca, ne intrakavernozna kinetika leka Invicorp.",
			bs: "Domschke et al., Gut 1978: nestajanje iz plazme nakon intravenske infuzije kod četiri zdrava dobrovoljca, prosječno poluvrijeme oko jedne minute; mala studijska referenca, ne intrakavernozna kinetika lijeka Invicorp.",
			mk: "Domschke et al., Gut 1978: исчезнување од плазмата по интравенска инфузија кај четири здрави доброволци, просечно полувреме од околу една минута; мала студиска референца, не интракавернозна кинетика на Invicorp.",
			lt: "Domschke et al., Gut 1978: išnykimas iš plazmos po intraveninės infuzijos keturiems sveikiems savanoriams, vidutinė pusėjimo trukmė apie vieną minutę. Šis mažas IV tyrimas nėra intrakaverninio Invicorp farmakokinetinė vertė ar bendra VIP tiriamosios medžiagos nuoroda.",
			lv: "Domschke et al., Gut 1978: izzušana no plazmas pēc intravenozas infūzijas četriem veseliem brīvprātīgajiem, vidējais pussabrukšanas laiks aptuveni viena minūte. Šis nelielais IV pētījums nav intrakavernozā Invicorp farmakokinētiskā vērtība vai vispārīga atsauce uz VIP pētniecības materiālu.",
			et: "Domschke et al., Gut 1978: plasmanäitajate langus pärast veenisisest infusiooni neljal tervel vabatahtlikul, keskmine poolväärtusaeg umbes üks minut. See väike IV-uuring ei ole intrakavernoosse Invicorpi farmakokineetiline väärtus ega üldine viide VIP-uurimismaterjalile.",
		},
		frequency: "—",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: isFiveProductLaunchLive("vip"),
		bergdorfProductSlug: isFiveProductLaunchLive("vip") ? "vip" : undefined,
		relatedPeptides: [],
		cas: "37221-79-7",
		molecularFormula: "C₁₄₇H₂₃₇N₄₃O₄₃S",
		molarMass: "3 327 g/mol",
		synonyms: ["Vasoactive intestinal peptide", "Aviptadil"],
		studyReferences: [
			{
				author: "Domschke et al.",
				journal: "Gut",
				year: 1978,
				title: "Vasoactive intestinal peptide in man: pharmacokinetics, metabolic and circulatory effects",
				url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC1412244/",
			},
			{
				author: "Sreedharan et al.",
				journal: "Biochem Biophys Res Commun",
				year: 1993,
				title: "Cloning and functional expression of a human VIP receptor",
				url: "https://pubmed.ncbi.nlm.nih.gov/8390245/",
			},
			{
				author: "Nicole et al.",
				journal: "Peptides",
				year: 2000,
				title: "Alanine scan of VIP interaction with human VPAC1 and VPAC2 receptors",
				url: "https://pubmed.ncbi.nlm.nih.gov/10801840/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "retatrutide",
		name: {
			en: "Retatrutide",
			de: "Retatrutide",
			it: "Retatrutide",
			es: "Retatrutida",
			fr: "Rétatrutide",
			nl: "Retatrutide",
			pt: "Retatrutida",
			"pt-br": "Retatrutida",
			pl: "Retatrutyd",
			sv: "Retatrutid",
			da: "Retatrutid",
			el: "Retatrutide",
			cs: "Retatrutid",
			fi: "Retatrutidi",
			ro: "Retatrutide",
			hu: "Retatrutid",
			sk: "Retatrutid",
			tr: "Retatrutid",
			nb: "Retatrutid",
			bg: "Retatrutide",
			hr: "Retatrutid",
			sl: "Retatrutide",
			sr: "Retatrutide",
			bs: "Retatrutide",
			mk: "Ретатрутид (Retatrutide)",
			lt: "Retatrutide",
			lv: "Retatrutide",
			et: "Retatrutide",
		},
		category: {
			en: "GLP-1/GIP/GCG",
			de: "GLP-1/GIP/GCG",
			it: "GLP-1/GIP/GCG",
			es: "GLP-1/GIP/GCG",
			fr: "GLP-1/GIP/GCG",
			nl: "GLP-1/GIP/GCG",
			pt: "GLP-1/GIP/GCG",
			"pt-br": "GLP-1/GIP/GCG",
			pl: "GLP-1/GIP/GCG",
			sv: "GLP-1/GIP/GCG",
			da: "GLP-1/GIP/GCG",
			el: "GLP-1/GIP/GCG",
			cs: "GLP-1/GIP/GCG",
			fi: "GLP-1/GIP/GCG",
			ro: "GLP-1/GIP/GCG",
			hu: "GLP-1/GIP/GCG",
			sk: "GLP-1/GIP/GCG",
			tr: "GLP-1/GIP/GCG",
			nb: "GLP-1/GIP/GCG",
			bg: "GLP-1/GIP/GCG",
			hr: "GLP-1/GIP/GCG",
			sl: "GLP-1/GIP/GCG",
			sr: "GLP-1/GIP/GCG",
			bs: "GLP-1/GIP/GCG",
			mk: "GLP-1/GIP/GCG",
			lt: "GLP-1/GIP/GCG",
			lv: "GLP-1/GIP/GCG",
			et: "GLP-1/GIP/GCG",
		},
		defaultVialSizes: [2, 5, 10, 15],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.5,
		doseRange: { min: 0.5, max: 12 },
		doseUnit: "mg",
		dosePresets: [0.5, 1, 2, 4, 8],
		halfLife: "~6 days",
		halfLifeHours: 144,
		halfLifeSource:
			"Coskun et al., Cell Metab 2022, Table 1: six single-subcutaneous-administration cohorts of healthy adults, geometric mean half-lives 134 to 165 hours. About six days, or 144 hours, is an approximate study reference, not an exact universal mean. Jastreboff et al. (2023) was a phase 2 trial, not a TRIUMPH phase 3 study.",
		halfLifeSourceLocalized: {
			en: "Coskun et al., Cell Metab 2022, Table 1: six single-subcutaneous-administration cohorts of healthy adults, geometric mean half-lives 134 to 165 hours. About six days, or 144 hours, is an approximate study reference, not an exact universal mean. Jastreboff et al. (2023) was a phase 2 trial, not a TRIUMPH phase 3 study.",
			de: "Coskun et al., Cell Metab 2022, Tabelle 1: gesunde Erwachsene, einmalige SC-Studienverabreichung; geometrische mittlere Halbwertszeiten je Kohorte 134–165 h. Rund 6 Tage (144 h) sind eine ungefähre Studienreferenz, kein exakt gemessener allgemeiner Mittelwert und kein TRIUMPH-Resultat.",
			it: "Coskun et al., Cell Metab 2022, tabella 1: adulti sani, singola somministrazione sottocutanea, sei coorti; medie geometriche dell'emivita 134-165 ore. 144 ore, circa sei giorni, è un riferimento approssimativo, non una media universale esatta. Jastreboff 2023 era fase 2, non TRIUMPH.",
			es: "Coskun et al., Cell Metab 2022, tabla 1: seis cohortes de adultos sanos con administración subcutánea única; medias geométricas de semivida de 134 a 165 horas. Las 144 horas, unos seis días, son una referencia aproximada del estudio, no una media universal medida exactamente.",
			fr: "Coskun et al., Cell Metab 2022, tableau 1 : administration sous-cutanée unique dans six cohortes d'adultes sains, moyennes géométriques de 134 à 165 h ; environ six jours, soit 144 h, est une référence approximative, pas une moyenne universelle exacte.",
			nl: "Coskun et al., Cell Metab 2022, tabel 1: gezonde volwassenen na één subcutane toediening, geometrische gemiddelden van 134 tot 165 uur over zes cohorten; 144 uur is een afgeronde referentie van ongeveer zes dagen, geen universeel gemeten gemiddelde",
			pt: "Coskun et al., Cell Metab 2022, tabela 1: adultos saudáveis, administração subcutânea única, seis coortes, médias geométricas de 134 a 165 horas. 144 horas corresponde apenas a uma referência aproximada de seis dias, não a uma média universal medida.",
			"pt-br":
				"Coskun et al., Cell Metab 2022, tabela 1: adultos saudáveis, administração subcutânea única, seis grupos. Meias-vidas médias geométricas de 134 a 165 horas; 144 horas, cerca de seis dias, é uma referência aproximada de estudo, não uma média universal medida.",
			pl: "Coskun et al., Cell Metab 2022, tabela 1: sześć kohort zdrowych dorosłych po pojedynczym podaniu podskórnym, geometryczne średnie 134–165 godzin. 144 godziny, czyli około sześciu dni, to przybliżona referencja badania, nie dokładny uniwersalny średni wynik. Jastreboff 2023 dotyczyło fazy 2, nie TRIUMPH.",
			sv: "Coskun et al., Cell Metab 2022, tabell 1: friska vuxna i sex kohorter efter en enda subkutan tillförsel. Geometriska medelhalveringstider 134 till 165 timmar. 144 timmar, omkring sex dagar, är en ungefärlig studiereferens och inget exakt universellt mätmedelvärde.",
			da: "Coskun et al., Cell Metab 2022, tabel 1: raske voksne efter en enkelt subkutan indgift, seks kohorter; geometriske middelhalveringstider 134 til 165 timer. 144 timer, cirka seks dage, er en tilnærmet forsøgsreference og ikke en præcist målt universel middelværdi.",
			el: "Coskun et al., Cell Metab 2022, πίνακας 1: υγιείς ενήλικες, έξι ομάδες εφάπαξ υποδόριας χορήγησης, γεωμετρικοί μέσοι χρόνοι ημιζωής 134 έως 165 ώρες. Οι 144 ώρες, περίπου έξι ημέρες, είναι προσεγγιστική αναφορά μελέτης, όχι καθολικός μετρημένος μέσος όρος.",
			cs: "Coskun et al., Cell Metab 2022, tabulka 1: zdraví dospělí po jednorázovém subkutánním podání, šest skupin; geometrické průměry 134 až 165 hodin. Přibližně šest dnů, tedy 144 hodin, je orientační studijní reference, nikoli univerzální měřený průměr. Jastreboff 2023 je studie fáze 2, ne TRIUMPH.",
			fi: "Coskun et al., Cell Metab 2022, taulukko 1: terveet aikuiset, yksittäinen ihonalainen tutkimusanto kuudessa ryhmässä, geometriset keskimääräiset puoliintumisajat 134–165 tuntia. 144 tuntia eli noin kuusi päivää on likimääräinen tutkimusviite, ei yleinen tarkka keskiarvo.",
			ro: "Coskun et al., Cell Metab 2022, tabelul 1: șase cohorte de adulți sănătoși după administrare subcutanată unică; medii geometrice 134-165 de ore. Circa șase zile, 144 de ore, este o referință aproximativă, nu un timp universal măsurat.",
			hu: "Coskun et al., Cell Metab 2022, 1. táblázat: hat egészséges felnőttkohorsz, egyszeri bőr alatti bejuttatás; geometriai átlagos felezési idők 134–165 óra. A 144 óra közelítő hatnapos referencia, nem univerzális mért átlag. Jastreboff 2023 2. fázisú vizsgálat volt, nem TRIUMPH.",
			sk: "Coskun et al., Cell Metab 2022, tabuľka 1: zdraví dospelí, jednorazové podkožné podanie v šiestich kohortách; geometrické priemery 134 až 165 hodín. Približne šesť dní alebo 144 hodín je orientačná študijná referencia, nie univerzálny presný priemer.",
			tr: "Coskun et al., Cell Metab 2022, Tablo 1: sağlıklı yetişkinlerde altı tek deri altı uygulama kohortunun geometrik ortalamaları 134–165 saat. Yaklaşık altı gün veya 144 saat yaklaşık çalışma referansıdır; kesin evrensel ortalama değildir. Jastreboff 2023 faz 2'dir, TRIUMPH çalışması değildir.",
			nb: "Coskun et al., Cell Metab 2022, tabell 1: seks kohorter av friske voksne etter én subkutan studietilførsel; geometriske middelverdier 134–165 timer. 144 timer er en tilnærmet seksdagersreferanse, ikke en eksakt universell middelverdi.",
			bg: "Coskun et al., Cell Metab 2022, таблица 1: здрави възрастни, еднократно подкожно въвеждане, шест кохорти; геометрични средни 134-165 h. Около шест дни, 144 h, е приблизителна референция, не точно измерена универсална средна стойност. Jastreboff 2023 е фаза 2, не TRIUMPH.",
			hr: "Coskun et al., Cell Metab 2022, tablica 1: zdravi odrasli nakon pojedinačne potkožne primjene; geometrijski prosjeci šest skupina 134 do 165 sati. 144 sata približna je šestodnevna referenca, ne univerzalno izmjereni prosjek; Jastreboff 2023. bio je faza 2, ne TRIUMPH.",
			sl: "Coskun et al., Cell Metab 2022, razpredelnica 1: zdravi odrasli po enkratni podkožni uporabi; geometrijska povprečja razpolovnega časa šestih skupin 134 do 165 ur. 144 ur je približna šestdnevna referenca, ne natančno izmerjeno splošno povprečje; Jastreboff 2023 je bila faza 2, ne TRIUMPH.",
			sr: "Coskun et al., Cell Metab 2022, tabela 1: zdravi odrasli nakon jednokratne potkožne primene; geometrijske sredine poluvremena šest kohorti 134 do 165 sati. 144 sata je približna šestodnevna referenca, ne tačno izmeren univerzalni prosek; Jastreboff 2023. bila je faza 2, ne TRIUMPH.",
			bs: "Coskun et al., Cell Metab 2022, tabela 1: zdravi odrasli nakon jednokratne potkožne primjene; geometrijske sredine poluvremena šest kohorti 134 do 165 sati. 144 sata je približna šestodnevna referenca, ne tačno izmjeren univerzalni prosjek; Jastreboff 2023. bila je faza 2, ne TRIUMPH.",
			mk: "Coskun et al., Cell Metab 2022, табела 1: здрави возрасни по еднократна поткожна примена; геометриски средни вредности на полувремето за шест кохорти 134 до 165 часа. 144 часа е приближна шестдневна референца, не точно измерен универзален просек; Jastreboff 2023 беше фаза 2, не TRIUMPH.",
			lt: "Coskun et al., Cell Metab 2022, 1 lentelė: šešios sveikų suaugusiųjų kohortos po vienkartinės po oda injekcijos, geometrinis pusėjimo trukmės vidurkis 134 iki 165 valandų. Maždaug šešios dienos, arba 144 valandos, yra apytikslė tyrimo nuoroda, o ne tikslus universalus vidurkis. Jastreboff et al. (2023) buvo 2 fazės tyrimas, o ne TRIUMPH 3 fazės tyrimas.",
			lv: "Coskun et al., Cell Metab 2022, 1. tabula: sešas veselu pieaugušo kohortas pēc vienreizējas subkutānas injekcijas, pussabrukšanas laika ģeometriskais vidējais 134 līdz 165 stundas. Apmēram sešas dienas jeb 144 stundas ir aptuvena pētījuma atsauce, nevis precīzs universāls vidējais rādītājs. Jastreboff et al. (2023) bija 2. fāzes pētījums, nevis TRIUMPH 3. fāzes pētījums.",
			et: "Coskun et al., Cell Metab 2022, tabel 1: kuus tervete täiskasvanute kohorti ühekordse nahaaluse manustamise järel, poolväärtusaja geomeetriline keskmine 134 kuni 165 tundi. Ligikaudu kuus päeva ehk 144 tundi on ligikaudne uuringuviide, mitte täpne üldine keskmine. Jastreboff et al. (2023) oli 2. faasi uuring, mitte TRIUMPH 3. faasi uuring.",
		},
		frequency: "weekly",
		titrationSchema: [
			{ week: 1, durationWeeks: 4, dose: 0.5 },
			{ week: 5, durationWeeks: 4, dose: 1 },
			{ week: 9, durationWeeks: 4, dose: 2 },
			{ week: 13, durationWeeks: 4, dose: 4 },
			{ week: 17, durationWeeks: 4, dose: 8 },
			{ week: 21, durationWeeks: 4, dose: 12 },
		],
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "retatrutide",
		relatedPeptides: ["semaglutid", "tirzepatid"],
		cas: "2381089-83-2",
		molecularFormula: "C₂₂₁H₃₄₂N₄₆O₆₈",
		molarMass: "4 731 g/mol",
		synonyms: ["LY3437943", "LY-3437943"],
		studyReferences: [
			{
				author: "Jastreboff et al.",
				journal: "NEJM",
				year: 2023,
				title: "Triple-Hormone-Receptor Agonist Retatrutide for Obesity",
				url: "https://pubmed.ncbi.nlm.nih.gov/37366315/",
			},
			{
				author: "Coskun et al.",
				journal: "Cell Metab",
				year: 2022,
				title: "LY3437943 Triple GIP/GLP-1/Glucagon Receptor Agonist Discovery",
				url: "https://pubmed.ncbi.nlm.nih.gov/35985340/",
			},
			{
				author: "Rosenstock et al.",
				journal: "Lancet",
				year: 2023,
				title: "Retatrutide Phase 2 Trial in Type 2 Diabetes",
				url: "https://pubmed.ncbi.nlm.nih.gov/37385280/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "cagrilintide",
		name: {
			en: "Cagrilintide",
			de: "Cagrilintid",
			it: "Cagrilintide",
			es: "Cagrilintide",
			fr: "Cagrilintide",
			nl: "Cagrilintide",
			pt: "Cagrilintide",
			"pt-br": "Cagrilintida",
			pl: "Cagrilintide",
			sv: "Cagrilintide",
			da: "Cagrilintide",
			el: "Cagrilintide",
			cs: "Cagrilintid",
			fi: "Kagrilintidi",
			ro: "Cagrilintide",
			hu: "Cagrilintide",
			sk: "Cagrilintide",
			tr: "Cagrilintide",
			nb: "Cagrilintide",
			bg: "Cagrilintide",
			hr: "Kagrilintid",
			sl: "Cagrilintide",
			sr: "Cagrilintide",
			bs: "Cagrilintide",
			mk: "Кагрилинтид (Cagrilintide)",
			lt: "Cagrilintide",
			lv: "Cagrilintide",
			et: "Cagrilintide",
		},
		category: {
			en: "Amylin Analogue",
			de: "Amylin-Analogon",
			it: "Analogo dell'amilina",
			es: "Análogo de amilina",
			fr: "Analogue de l'amyline",
			nl: "Amyline-analoog",
			pt: "Análogo da amilina",
			"pt-br": "Análogo da amilina",
			pl: "Analog amyliny",
			sv: "Amylinanalog",
			da: "Amylinanalog",
			el: "Ανάλογο αμυλίνης",
			cs: "Analog amylinu",
			fi: "Amyliinianalogi",
			ro: "Analog de amilină",
			hu: "Amilinanalóg",
			sk: "Analóg amylínu",
			tr: "Amilin analoğu",
			nb: "Amylinanalog",
			bg: "Аналог на амилин",
			hr: "Analog amilina",
			sl: "Analog amilina",
			sr: "Analog amilina",
			bs: "Analog amilina",
			mk: "Аналог на амилин",
			lt: "Amilino analogas",
			lv: "Amilīna analogs",
			et: "Amüliini analoog",
		},
		calculatorMode: "dose",
		defaultVialSizes: [5],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.3,
		doseUnit: "mg",
		halfLife: "~180 hours (159–195 h study range)",
		halfLifeHours: 180,
		halfLifeSource:
			"Enebo et al., Lancet 2021: subcutaneous clinical trial cagrilintide coadministered with semaglutide, reported terminal half-life range 159 to 195 hours. The 180-hour reference represents that range; it is not a separately measured mean or a general value for cagrilintide research material.",
		halfLifeSourceLocalized: {
			en: "Enebo et al., Lancet 2021: subcutaneous clinical trial cagrilintide coadministered with semaglutide, reported terminal half-life range 159 to 195 hours. The 180-hour reference represents that range; it is not a separately measured mean or a general value for cagrilintide research material.",
			de: "Enebo et al., Lancet 2021: klinische SC-Cagrilintidformulierung bei gleichzeitiger Semaglutid-Exposition; berichtete terminale Halbwertszeiten 159–195 h. 180 h ist eine repräsentative Referenz innerhalb dieses Bereichs, kein separat gemessener universeller Mittelwert.",
			it: "Enebo et al., Lancet 2021: cagrilintide sottocutanea in concomitanza con semaglutide, preparato clinico sperimentale; emivite riportate 159-195 ore. 180 ore è un riferimento rappresentativo nell'intervallo, non una media misurata separatamente.",
			es: "Enebo et al., Lancet 2021: formulación clínica de Cagrilintide administrada por vía subcutánea junto con semaglutida; semivida comunicada de 159 a 195 horas. Las 180 horas representan una referencia dentro del intervalo, no una nueva media medida ni un valor propio de cualquier material.",
			fr: "Enebo et al., Lancet 2021 : cagrilintide sous-cutané formulé pour l'étude, avec sémaglutide concomitant ; demi-vies rapportées de 159 à 195 h. La référence de 180 h représente ce domaine, sans être une moyenne mesurée distincte.",
			nl: "Enebo et al., Lancet 2021: subcutaan cagrilintide met gelijktijdig semaglutide, klinische studiebereiding; gerapporteerd bereik 159 tot 195 uur; 180 uur is een representatieve referentie binnen dit bereik, geen afzonderlijk gemeten gemiddelde",
			pt: "Enebo et al., Lancet 2021: preparação de estudo de Cagrilintide subcutâneo com semaglutida concomitante; semivida terminal descrita entre 159 e 195 horas. 180 horas é uma referência representativa dentro do intervalo, não uma média medida independente.",
			"pt-br":
				"Enebo et al., Lancet 2021: preparação clínica de cagrilintida subcutânea com administração concomitante de semaglutida; intervalo relatado de 159 a 195 horas. A referência de 180 horas é representativa desse intervalo, não uma média medida independente nem um valor de qualquer material de pesquisa.",
			pl: "Enebo et al., Lancet 2021: podskórny Cagrilintide w skojarzeniu z semaglutydem, preparaty kliniczne, zgłoszony zakres 159–195 godzin. 180 godzin jest reprezentatywną referencją w tym zakresie, nie osobno zmierzoną średnią ani stałą dowolnego produktu.",
			sv: "Enebo et al., Lancet 2021: subkutant cagrilintide som kliniskt studiepreparat med samtidig semaglutidtillförsel, rapporterat intervall 159 till 195 timmar. 180 timmar är en representativ referens inom intervallet, inte ett separat uppmätt medelvärde.",
			da: "Enebo et al., Lancet 2021: subkutant cagrilintid sammen med semaglutid i kliniske forsøgspræparater; rapporteret terminal halveringstid 159 til 195 timer. 180 timer er en repræsentativ reference inden for intervallet og ikke en selvstændigt målt middelværdi.",
			el: "Enebo et al., Lancet 2021: υποδόρια καγκριλιντίδη με συγχορήγηση σεμαγλουτίδης σε κλινικό πειραματικό σκεύασμα, αναφερόμενοι χρόνοι 159 έως 195 ώρες. Οι 180 ώρες είναι αντιπροσωπευτική αναφορά μέσα στο εύρος και όχι χωριστός μετρημένος μέσος όρος.",
			cs: "Enebo et al., Lancet 2021: klinický studijní přípravek cagrilintidu při subkutánním souběžném podávání se semaglutidem; uváděný rozsah 159 až 195 hodin. Hodnota 180 hodin je reprezentativní reference v tomto rozmezí, nikoli samostatně naměřený průměr.",
			fi: "Enebo et al., Lancet 2021: ihonalainen kagrilintidin tutkimusvalmiste samanaikaisesti semaglutidin kanssa, raportoitu puoliintumisaika 159–195 tuntia. 180 tuntia on aluetta edustava tutkimusviite, ei erikseen mitattu keskiarvo.",
			ro: "Enebo et al., Lancet 2021: preparat clinic Cagrilintide subcutanat în asociere cu semaglutidă, interval raportat 159-195 de ore; 180 de ore este o referință reprezentativă în interval, fără a fi o medie măsurată separată.",
			hu: "Enebo et al., Lancet 2021: bőr alatti cagrilintid szemaglutiddal együtt, klinikai vizsgálati készítménnyel; közölt felezésiidő-tartomány 159–195 óra. A 180 óra e tartományon belüli reprezentatív referencia, nem külön mért átlag.",
			sk: "Enebo et al., Lancet 2021: podkožný cagrilintid v klinickej formulácii so súbežným semaglutidom, uvedený rozsah 159 až 195 hodín. Hodnota 180 hodín je reprezentatívna referencia v rozsahu, nie samostatne nameraný priemer.",
			tr: "Enebo et al., Lancet 2021: klinik çalışma formülasyonundaki deri altı Cagrilintide, semaglutidle birlikte uygulandığında 159–195 saat bildirilmiştir. 180 saat bu aralık içindeki temsilî referanstır; ayrıca ölçülmüş ortalama veya tek başına kullanımın sabiti değildir.",
			nb: "Enebo et al., Lancet 2021: subkutant cagrilintid i klinisk studieformulering, samtidig med semaglutid; rapportert halveringstid 159–195 timer. 180 timer er en representativ referanse innen området, ikke et eget målt gjennomsnitt.",
			bg: "Enebo et al., Lancet 2021: подкожно изпитван фармацевтичен кагрилинтид с едновременно въвеждане на семаглутид; отчетен терминален полуживот 159-195 h. 180 h е представителна референция в този диапазон, не отделна измерена средна стойност.",
			hr: "Enebo et al., Lancet 2021: klinički potkožni kagrilintid uz istodobni semaglutid, opisani raspon 159 do 195 sati. 180 sati reprezentativna je referenca unutar tog raspona, ne zaseban izmjereni prosjek ili konstanta proizvoda.",
			sl: "Enebo et al., Lancet 2021: klinični podkožni kagrilintid (Cagrilintide) ob sočasnem semaglutidu (Semaglutide); opisani razpon terminalnega razpolovnega časa 159 do 195 ur. 180 ur je reprezentativna referenca znotraj tega razpona, ne ločeno izmerjeno povprečje ali konstanta izdelka.",
			sr: "Enebo et al., Lancet 2021: klinički potkožni kagrilintid (Cagrilintide) uz istovremeni semaglutid (Semaglutide); opisani raspon terminalnog poluvremena 159 do 195 sati. 180 sati je reprezentativna referenca unutar tog raspona, ne zasebno izmeren prosek ili konstanta proizvoda.",
			bs: "Enebo et al., Lancet 2021: klinički potkožni kagrilintid (Cagrilintide) uz istovremeni semaglutid (Semaglutide); opisani raspon terminalnog poluvremena 159 do 195 sati. 180 sati je reprezentativna referenca unutar tog raspona, ne odvojeno izmjeren prosjek ili konstanta proizvoda.",
			mk: "Enebo et al., Lancet 2021: клинички поткожен кагрилинтид (Cagrilintide) со истовремен семаглутид (Semaglutide); опишан опсег на терминалното полувреме 159 до 195 часа. 180 часа е репрезентативна референца во тој опсег, не одделно измерен просек или константа на производот.",
			lt: "Enebo et al., Lancet 2021: klinikinis po oda vartojamas Cagrilintide kartu su Semaglutide, nurodytas terminalinės pusėjimo trukmės intervalas 159 iki 195 valandų. 180 valandų nuoroda atspindi šį intervalą; tai nėra atskirai išmatuotas vidurkis ar bendra Cagrilintide tiriamosios medžiagos vertė.",
			lv: "Enebo et al., Lancet 2021: klīnisks subkutāni lietots Cagrilintide kopā ar Semaglutide, norādītais terminālā pussabrukšanas laika diapazons 159 līdz 195 stundas. 180 stundu atsauce atspoguļo šo diapazonu; tas nav atsevišķi mērīts vidējais lielums vai vispārīga Cagrilintide pētniecības materiāla vērtība.",
			et: "Enebo et al., Lancet 2021: kliinilises uuringus nahaalune Cagrilintide koos Semaglutidega, teatatud terminaalse poolväärtusaja vahemik 159 kuni 195 tundi. 180 tunni viide esindab seda vahemikku; see ei ole eraldi mõõdetud keskmine ega üldine Cagrilintide-uurimismaterjali väärtus.",
		},
		frequency: "—",
		recommendedSyringe: 0.5,
		hasBergdorfProduct: isFiveProductLaunchLive("cagrilintide"),
		bergdorfProductSlug: isFiveProductLaunchLive("cagrilintide") ? "cagrilintide" : undefined,
		relatedPeptides: [],
		cas: "1415456-99-3",
		molecularFormula: "C₁₉₄H₃₁₂N₅₄O₅₉S₂",
		molarMass: "4 409 g/mol",
		synonyms: ["AM833"],
		studyReferences: [
			{
				author: "Kruse et al.",
				journal: "J Med Chem",
				year: 2021,
				title: "Development of Cagrilintide, a Long-Acting Amylin Analogue",
				url: "https://pubmed.ncbi.nlm.nih.gov/34288673/",
			},
			{
				author: "Fletcher et al.",
				journal: "J Pharmacol Exp Ther",
				year: 2021,
				title: "AM833 Is a Novel Agonist of Calcitonin Family G Protein-Coupled Receptors",
				url: "https://pubmed.ncbi.nlm.nih.gov/33727283/",
			},
			{
				author: "Enebo et al.",
				journal: "Lancet",
				year: 2021,
				title:
					"Safety, tolerability, pharmacokinetics, and pharmacodynamics of cagrilintide with semaglutide",
				url: "https://pubmed.ncbi.nlm.nih.gov/33894838/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "semaglutid",
		name: {
			en: "Semaglutide",
			de: "Semaglutid",
			it: "Semaglutide",
			es: "Semaglutida",
			fr: "Sémaglutide",
			nl: "Semaglutide",
			pt: "Semaglutida",
			"pt-br": "Semaglutida",
			pl: "Semaglutyd",
			sv: "Semaglutid",
			da: "Semaglutid",
			el: "Semaglutide",
			cs: "Semaglutid",
			fi: "Semaglutidi",
			ro: "Semaglutidă",
			hu: "Szemaglutid",
			sk: "Semaglutid",
			tr: "Semaglutid",
			nb: "Semaglutid",
			bg: "Semaglutide",
			hr: "Semaglutid",
			sl: "Semaglutide",
			sr: "Semaglutide",
			bs: "Semaglutide",
			mk: "Семаглутид (Semaglutide)",
			lt: "Semaglutide",
			lv: "Semaglutide",
			et: "Semaglutide",
		},
		category: {
			en: "GLP-1",
			de: "GLP-1",
			it: "GLP-1",
			es: "GLP-1",
			fr: "GLP-1",
			nl: "GLP-1",
			pt: "GLP-1",
			"pt-br": "GLP-1",
			pl: "GLP-1",
			sv: "GLP-1",
			da: "GLP-1",
			el: "GLP-1",
			cs: "GLP-1",
			fi: "GLP-1",
			ro: "GLP-1",
			hu: "GLP-1",
			sk: "GLP-1",
			tr: "GLP-1",
			nb: "GLP-1",
			bg: "GLP-1",
			hr: "GLP-1",
			sl: "GLP-1",
			sr: "GLP-1",
			bs: "GLP-1",
			mk: "GLP-1",
			lt: "GLP-1",
			lv: "GLP-1",
			et: "GLP-1",
		},
		defaultVialSizes: [2, 5, 10],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.25,
		doseRange: { min: 0.25, max: 2.4 },
		doseUnit: "mg",
		dosePresets: [0.25, 0.5, 1, 1.7, 2.4],
		halfLife: "~7 days",
		halfLifeHours: 168,
		halfLifeSource:
			"Ozempic US Prescribing Information, May 2026, section 12.3: the formulated subcutaneous medicine has an elimination half-life of approximately one week. The 168-hour reference concerns Ozempic; it does not establish kinetics of oral or intranasal formulations, salt material or a particular research product.",
		halfLifeSourceLocalized: {
			en: "Ozempic US Prescribing Information, May 2026, section 12.3: the formulated subcutaneous medicine has an elimination half-life of approximately one week. The 168-hour reference concerns Ozempic; it does not establish kinetics of oral or intranasal formulations, salt material or a particular research product.",
			de: "Ozempic-US-Fachinformation 05/2026, Abschnitt 12.3: formuliertes subkutanes Semaglutidarzneimittel, Eliminationshalbwertszeit ungefähr 1 Woche (168 h als Näherung). Keine Übertragung auf beliebige Salze, andere Routen oder uncharakterisiertes Forschungsmaterial.",
			it: "Ozempic US Prescribing Information 05/2026, sezione 12.3: medicinale sottocutaneo formulato, emivita di eliminazione di circa una settimana; 168 ore come riferimento di Ozempic. Non stabilisce valori per vie orali o intranasali, sali o materiali dello shop.",
			es: "Ozempic US Prescribing Information, mayo de 2026, apartado 12.3: medicamento formulado subcutáneo, semivida de eliminación aproximada de una semana. Las 168 horas son esa referencia farmacéutica; no describen otras vías, sales ni material de investigación.",
			fr: "Ozempic, informations américaines de mai 2026, section 12.3 : médicament formulé sous-cutané, demi-vie d'élimination approximative d'une semaine, soit 168 h comme référence ; sans transposition aux formes orales, intranasales, salines ou à un matériau de recherche.",
			nl: "Ozempic US Prescribing Information, mei 2026, paragraaf 12.3: geformuleerd subcutaan geneesmiddel, eliminatiehalfwaardetijd ongeveer één week; 168 uur is deze geneesmiddelreferentie en geen waarde voor orale of intranasale routes, zoutmateriaal of ander onderzoeksmateriaal",
			pt: "Ozempic, informação norte-americana de maio de 2026, secção 12.3: medicamento formulado subcutâneo, semivida de eliminação de aproximadamente uma semana. 168 horas é essa referência aproximada, sem demonstrar valores de formas orais, intranasais, sais ou material de investigação.",
			"pt-br":
				"Ozempic US Prescribing Information, maio de 2026, seção 12.3: medicamento formulado subcutâneo, meia-vida de eliminação de aproximadamente uma semana. As 168 horas são uma referência dessa preparação; não comprovam equivalência com formas orais, intranasais, sais ou produtos de pesquisa.",
			pl: "Ozempic, amerykańska informacja o leku 05/2026, punkt 12.3: określony preparat podskórny, okres półtrwania eliminacji około tygodnia. 168 godzin to przybliżona referencja Ozempic, nie wynik dla drogi doustnej lub donosowej, soli albo materiału badawczego.",
			sv: "Ozempic US Prescribing Information, maj 2026, avsnitt 12.3: formulerat subkutant läkemedel, eliminationshalveringstid ungefär en vecka. 168 timmar är denna Ozempic-referens, inte ett oralt eller intranasalt värde och inte en egenskap fastställd för saltmaterial eller en forskningsprodukt.",
			da: "Ozempic, amerikansk produktinformation fra maj 2026, afsnit 12.3: formuleret subkutant lægemiddel, eliminationshalveringstid cirka én uge. 168 timer er denne tilnærmede Ozempic-reference og ikke dokumentation for oral eller intranasal indgift, saltmateriale eller et forskningsprodukt.",
			el: "Ozempic US Prescribing Information 05/2026, ενότητα 12.3: χρόνος ημιζωής αποβολής περίπου μία εβδομάδα για το συγκεκριμένο υποδόριο φάρμακο. Οι 168 ώρες είναι προσεγγιστική αναφορά Ozempic και δεν χαρακτηρίζουν από του στόματος ή ενδορρινική έκθεση, άλλα άλατα ή υλικό του καταστήματος.",
			cs: "Ozempic US Prescribing Information, květen 2026, oddíl 12.3: formulovaný subkutánní léčivý přípravek, eliminační poločas přibližně jeden týden. Hodnota 168 hodin se vztahuje k této lékové referenci; neprokazuje perorální či intranazální kinetiku ani vlastnosti soli nebo jiného výzkumného materiálu.",
			fi: "Ozempicin Yhdysvaltain valmisteinformaatio 05/2026, kohta 12.3: valmistettu ihonalainen lääke, eliminaatiopuoliintumisaika noin viikko eli 168 tuntia. Tämä valmisteviite ei osoita suun kautta tai intranasaalisesti annetun aineen, suolamuodon tai erillisen tutkimustuotteen ominaisuuksia.",
			ro: "Ozempic, informația de prescriere SUA 05/2026, secțiunea 12.3: medicament formulat subcutanat, eliminare cu timp de înjumătățire de circa o săptămână; 168 de ore este referința aproximativă a acestui preparat, fără transfer la alte căi sau materiale.",
			hu: "Ozempic amerikai előírás, 2026. május, 12.3. szakasz: a formulált, bőr alatti gyógyszer eliminációs felezési ideje körülbelül egy hét, azaz 168 óra. Nem orális vagy intranazális adat, és nem sóforma- vagy kutatásitermék-azonossági bizonyíték.",
			sk: "Ozempic, americká informácia o lieku z mája 2026, časť 12.3: formulovaný podkožný liek, eliminačný polčas približne jeden týždeň. Hodnota 168 hodín je prípravková referencia; nepreukazuje perorálne, intranazálne ani iné materiálové vlastnosti.",
			tr: "Ozempic ABD ürün bilgisi, Mayıs 2026, bölüm 12.3: formüle edilmiş deri altı ilaçta eliminasyon yarı ömrü yaklaşık bir hafta. 168 saat bu ilaç referansını temsil eder; oral, intranazal, tuz biçimi veya araştırma ürününün kimliği için değer çıkarılamaz.",
			nb: "Ozempic US Prescribing Information, mai 2026, punkt 12.3: formulert subkutant legemiddel, eliminasjonshalveringstid omtrent én uke. 168 timer er denne preparatreferansen, ikke en verdi for andre tilførselsveier eller ukjent forskningsmateriale.",
			bg: "Ozempic, американска информация 05/2026, раздел 12.3: формулирано подкожно лекарство, елиминационен полуживот приблизително една седмица. 168 h е тази приблизителна лекарствена референция, не перорална или интраназална стойност и не доказателство за солева форма или магазинен материал.",
			hr: "Ozempic, američke informacije o lijeku iz svibnja 2026., odjeljak 12.3: formulirani potkožni lijek, eliminacijski poluvijek približno jedan tjedan. 168 sati referenca je tog lijeka, ne dokaz za oralnu ili intranazalnu primjenu, sol ili identitet istraživačkog materijala.",
			sl: "Ozempic, ameriške informacije o zdravilu, maj 2026, razdelek 12.3: formulirano podkožno zdravilo, razpolovni čas izločanja približno en teden. 168 ur je referenca tega zdravila, ne dokaz za peroralno ali intranazalno uporabo, sol ali identiteto raziskovalnega materiala.",
			sr: "Ozempic, američke informacije o leku, maj 2026, odeljak 12.3: formulisani potkožni lek, poluvreme eliminacije približno jedna nedelja. 168 sati je referenca tog leka, ne dokaz za oralnu ili intranazalnu primenu, so ili identitet istraživačkog materijala.",
			bs: "Ozempic, američke informacije o lijeku, maj 2026, odjeljak 12.3: formulirani potkožni lijek, poluvrijeme eliminacije približno jedna sedmica. 168 sati je referenca tog lijeka, ne dokaz za oralnu ili intranazalnu primjenu, so ili identitet istraživačkog materijala.",
			mk: "Ozempic, американски информации за лекот, мај 2026, дел 12.3: формулиран поткожен лек, полувреме на елиминација приближно една недела. 168 часа е референца на тој лек, не доказ за орална или интраназална примена, сол или идентитет на истражувачки материјал.",
			lt: "Ozempic, JAV informacija apie vaistą, 2026 m. gegužė, 12.3 skirsnis: suformuluotas po oda vartojamas vaistas, eliminacijos pusėjimo trukmė apie vieną savaitę. 168 valandų nuoroda susijusi su Ozempic; ji nenustato geriamųjų ar intranazalinių formų, druskos medžiagos ar konkretaus tyrimo produkto kinetikos.",
			lv: "Ozempic, ASV zāļu informācija, 2026. gada maijs, 12.3. sadaļa: formulēts subkutāni lietojams medikaments, eliminācijas pussabrukšanas laiks aptuveni viena nedēļa. 168 stundu atsauce attiecas uz Ozempic; tā nenosaka iekšķīgi vai intranazāli lietojamu formu, sāls materiāla vai konkrēta pētniecības produkta kinētiku.",
			et: "Ozempic, USA ravimiteave, mai 2026, jaotis 12.3: formuleeritud nahaalune ravim, eliminatsiooni poolväärtusaeg ligikaudu üks nädal. 168 tunni viide puudutab Ozempicut; see ei määra suukaudse ega ninasisese vormi, soolamaterjali ega konkreetse uurimistoote kineetikat.",
		},
		frequency: "weekly",
		titrationSchema: [
			{ week: 1, durationWeeks: 4, dose: 0.25 },
			{ week: 5, durationWeeks: 4, dose: 0.5 },
			{ week: 9, durationWeeks: 4, dose: 1.0 },
			{ week: 13, durationWeeks: 4, dose: 1.7 },
			{ week: 17, durationWeeks: 4, dose: 2.4 },
		],
		recommendedSyringe: 0.3,
		hasBergdorfProduct: false,
		relatedPeptides: ["retatrutide", "tirzepatid"],
	},
	{
		slug: "tirzepatid",
		name: {
			en: "Tirzepatide",
			de: "Tirzepatid",
			it: "Tirzepatide",
			es: "Tirzepatida",
			fr: "Tirzépatide",
			nl: "Tirzepatide",
			pt: "Tirzepatida",
			"pt-br": "Tirzepatida",
			pl: "Tirzepatyd",
			sv: "Tirzepatid",
			da: "Tirzepatid",
			el: "Tirzepatide",
			cs: "Tirzepatid",
			fi: "Tirtsepatidi",
			ro: "Tirzepatidă",
			hu: "Tirzepatid",
			sk: "Tirzepatid",
			tr: "Tirzepatid",
			nb: "Tirzepatid",
			bg: "Tirzepatide",
			hr: "Tirzepatid",
			sl: "Tirzepatide",
			sr: "Tirzepatide",
			bs: "Tirzepatide",
			mk: "Тирзепатид (Tirzepatide)",
			lt: "Tirzepatide",
			lv: "Tirzepatide",
			et: "Tirzepatide",
		},
		category: {
			en: "GLP-1/GIP",
			de: "GLP-1/GIP",
			it: "GLP-1/GIP",
			es: "GLP-1/GIP",
			fr: "GLP-1/GIP",
			nl: "GLP-1/GIP",
			pt: "GLP-1/GIP",
			"pt-br": "GLP-1/GIP",
			pl: "GLP-1/GIP",
			sv: "GLP-1/GIP",
			da: "GLP-1/GIP",
			el: "GLP-1/GIP",
			cs: "GLP-1/GIP",
			fi: "GLP-1/GIP",
			ro: "GLP-1/GIP",
			hu: "GLP-1/GIP",
			sk: "GLP-1/GIP",
			tr: "GLP-1/GIP",
			nb: "GLP-1/GIP",
			bg: "GLP-1/GIP",
			hr: "GLP-1/GIP",
			sl: "GLP-1/GIP",
			sr: "GLP-1/GIP",
			bs: "GLP-1/GIP",
			mk: "GLP-1/GIP",
			lt: "GLP-1/GIP",
			lv: "GLP-1/GIP",
			et: "GLP-1/GIP",
		},
		defaultVialSizes: [10, 15, 20, 30, 60, 90],
		defaultVialSize: 15,
		defaultBacWater: 2,
		defaultDose: 2.5,
		doseRange: { min: 2.5, max: 15 },
		doseUnit: "mg",
		dosePresets: [2.5, 5, 7.5, 10, 15],
		halfLife: "~5 days",
		halfLifeHours: 120,
		halfLifeSource:
			"Mounjaro US Prescribing Information, August 2026, section 12.3: the formulated subcutaneous medicine has an elimination half-life of approximately five days. The label describes similar pharmacokinetics in healthy subjects and patients with type 2 diabetes. This product-specific reference replaces a generic SURPASS-1 attribution and does not establish research-material equivalence.",
		halfLifeSourceLocalized: {
			en: "Mounjaro US Prescribing Information, August 2026, section 12.3: the formulated subcutaneous medicine has an elimination half-life of approximately five days. The label describes similar pharmacokinetics in healthy subjects and patients with type 2 diabetes. This product-specific reference replaces a generic SURPASS-1 attribution and does not establish research-material equivalence.",
			de: "Mounjaro-US-Fachinformation 08/2026, Abschnitt 12.3: formuliertes SC-Arzneimittel, ähnlich beschriebene PK bei Gesunden und Typ-2-Diabetes, Eliminationshalbwertszeit etwa 5 Tage (120 h als Referenz). Kein allgemeiner Forschungsproduktwert.",
			it: "Mounjaro US Prescribing Information 08/2026, sezione 12.3: medicinale sottocutaneo formulato nel contesto clinico valutato; emivita di eliminazione di circa cinque giorni. Il riferimento è la specifica informazione del medicinale, non un'etichetta farmacocinetica generica SURPASS-1.",
			es: "Mounjaro US Prescribing Information, agosto de 2026, apartado 12.3: medicamento formulado subcutáneo, semivida de eliminación aproximada de cinco días. Referencia del preparado farmacéutico descrito, no una constante aplicable a toda preparación de tirzepatida.",
			fr: "Mounjaro, informations américaines d'août 2026, section 12.3 : médicament formulé sous-cutané, demi-vie d'élimination d'environ cinq jours ; référence du médicament évalué, pas une constante de tout matériau de tirzépatide.",
			nl: "Mounjaro US Prescribing Information, augustus 2026, paragraaf 12.3: geformuleerd subcutaan geneesmiddel in de beschreven patiëntenpopulatie, eliminatiehalfwaardetijd ongeveer vijf dagen; geneesmiddelgebonden referentie en geen universele onderzoeksproductwaarde",
			pt: "Mounjaro, informação norte-americana de agosto de 2026, secção 12.3: medicamento formulado subcutâneo nas condições clínicas avaliadas, semivida de eliminação de cerca de cinco dias. 120 horas é uma referência do medicamento, não uma constante para qualquer preparação ou população.",
			"pt-br":
				"Mounjaro US Prescribing Information, agosto de 2026, seção 12.3: medicamento formulado por via subcutânea, meia-vida de eliminação de aproximadamente cinco dias. A referência de 120 horas pertence a esse contexto farmacêutico e não é uma constante de toda preparação com tirzepatida.",
			pl: "Mounjaro, amerykańska informacja o leku 08/2026, punkt 12.3: określony lek podskórny, okres półtrwania eliminacji około pięciu dni. Referencja dotyczy ocenionego preparatu i jego populacji klinicznej, nie dowolnego materiału; źródłem parametru jest informacja o leku, nie etykieta SURPASS-1.",
			sv: "Mounjaro US Prescribing Information, augusti 2026, avsnitt 12.3: eliminationshalveringstid omkring fem dagar för det formulerade subkutana läkemedlet i produktinformationens bedömda läkemedelskontext. Referensen gäller preparatet och dess undersökta populationer, inte ett universellt värde för varje forskningsmaterial.",
			da: "Mounjaro, amerikansk produktinformation fra august 2026, afsnit 12.3: formuleret subkutant lægemiddel i produktinformationens undersøgte populationer; eliminationshalveringstid cirka fem dage. Præparatspecifik reference, ikke en universel værdi for ethvert tirzepatidmateriale.",
			el: "Mounjaro US Prescribing Information 08/2026, ενότητα 12.3: χρόνος ημιζωής αποβολής περίπου πέντε ημέρες για το συγκεκριμένο υποδόριο φάρμακο. Οι 120 ώρες είναι προσεγγιστική αναφορά του σκευάσματος, χωρίς αυτόματη ισοδυναμία άλλου ερευνητικού υλικού.",
			cs: "Mounjaro US Prescribing Information, srpen 2026, oddíl 12.3: formulovaný subkutánní léčivý přípravek, eliminační poločas přibližně pět dnů. Hodnota asi 120 hodin je referencí tohoto přípravku a jeho farmakokinetického hodnocení, nikoli univerzální údaj z SURPASS-1 pro jiný materiál.",
			fi: "Mounjaron Yhdysvaltain valmisteinformaatio 08/2026, kohta 12.3: valmistettu ihonalainen lääke, eliminaatiopuoliintumisaika noin viisi päivää. Lähde koskee nimettyä kliinistä valmistetta ja sen arvioituja olosuhteita, ei kaikkia samalla aineen nimellä kuvattuja materiaaleja.",
			ro: "Mounjaro, informația de prescriere SUA 08/2026, secțiunea 12.3: medicament formulat subcutanat, eliminare cu timp de înjumătățire de circa cinci zile; referință a preparatului evaluat, fără a caracteriza orice material cu tirzepatidă.",
			hu: "Mounjaro amerikai előírás, 2026. augusztus, 12.3. szakasz: a meghatározott bőr alatti gyógyszer eliminációs felezési ideje körülbelül öt nap. Készítményhez és vizsgálati populációhoz kötött referencia; a PK-forrás az előírás, nem a SURPASS-1 általános címkéje.",
			sk: "Mounjaro, americká informácia o lieku z augusta 2026, časť 12.3: formulovaný podkožný liek s tirzepatidom, eliminačný polčas približne päť dní. Ide o referenciu pomenovaného prípravku, nie univerzálnu hodnotu výskumného materiálu.",
			tr: "Mounjaro ABD ürün bilgisi, Ağustos 2026, bölüm 12.3: formüle edilmiş deri altı ilaçta eliminasyon yarı ömrü yaklaşık beş gün. Bu, ürün bilgisindeki klinik preparat referansıdır; SURPASS-1 etiketiyle veya farklı araştırma materyalinin kesin değeri olarak sunulmamalıdır.",
			nb: "Mounjaro US Prescribing Information, august 2026, punkt 12.3: formulert subkutant legemiddel; eliminasjonshalveringstid omtrent fem dager. Preparatspesifikk referanse fra preparatomtalen, ikke en generell materialverdi utledet av SURPASS-1.",
			bg: "Mounjaro, американска информация 08/2026, раздел 12.3: формулиран подкожен лекарствен препарат, елиминационен полуживот около пет дни, приблизително 120 h. Това е лекарствена референция, а не универсална характеристика на всяка проба; източникът е продуктовата информация, не SURPASS-1.",
			hr: "Mounjaro, američke informacije o lijeku iz kolovoza 2026., odjeljak 12.3: formulirani potkožni lijek, eliminacijski poluvijek približno pet dana. Izvor je farmaceutska informacija, ne PK-etiketa SURPASS-1; nije univerzalna vrijednost drugog materijala.",
			sl: "Mounjaro, ameriške informacije o zdravilu, avgust 2026, razdelek 12.3: formulirano podkožno zdravilo, razpolovni čas izločanja približno pet dni. Vir so informacije o zdravilu, ne PK-oznaka študije SURPASS-1; ni splošna vrednost drugega materiala.",
			sr: "Mounjaro, američke informacije o leku, avgust 2026, odeljak 12.3: formulisani potkožni lek, poluvreme eliminacije približno pet dana. Izvor su informacije o leku, ne PK-oznaka studije SURPASS-1; nije univerzalna vrednost drugog materijala.",
			bs: "Mounjaro, američke informacije o lijeku, august 2026, odjeljak 12.3: formulirani potkožni lijek, poluvrijeme eliminacije približno pet dana. Izvor su informacije o lijeku, ne PK-oznaka studije SURPASS-1; nije univerzalna vrijednost drugog materijala.",
			mk: "Mounjaro, американски информации за лекот, август 2026, дел 12.3: формулиран поткожен лек, полувреме на елиминација приближно пет дена. Извор се информациите за лекот, не PK-ознака на студијата SURPASS-1; не е универзална вредност на друг материјал.",
			lt: "Mounjaro, JAV informacija apie vaistą, 2026 m. rugpjūtis, 12.3 skirsnis: suformuluotas po oda vartojamas vaistas, eliminacijos pusėjimo trukmė apie penkias dienas. Informaciniame lapelyje aprašoma panaši farmakokinetika sveikiems asmenims ir pacientams, sergantiems 2 tipo cukriniu diabetu. Ši konkretaus produkto nuoroda pakeičia bendrą SURPASS-1 priskyrimą ir neįrodo tiriamosios medžiagos lygiavertiškumo.",
			lv: "Mounjaro, ASV zāļu informācija, 2026. gada augusts, 12.3. sadaļa: formulēts subkutāni lietojams medikaments, eliminācijas pussabrukšanas laiks aptuveni piecas dienas. Zāļu informācijā aprakstīta līdzīga farmakokinētika veseliem cilvēkiem un pacientiem ar 2. tipa cukura diabētu. Šī konkrētā produkta atsauce aizstāj vispārīgu SURPASS-1 attiecinājumu un nepierāda pētniecības materiāla ekvivalenci.",
			et: "Mounjaro, USA ravimiteave, august 2026, jaotis 12.3: formuleeritud nahaalune ravim, eliminatsiooni poolväärtusaeg ligikaudu viis päeva. Ravimiinfos kirjeldatakse sarnast farmakokineetikat tervetel isikutel ja 2. tüüpi diabeediga patsientidel. See konkreetse toote viide asendab üldist SURPASS-1 omistamist ega tõenda uurimismaterjali samaväärsust.",
		},
		frequency: "weekly",
		titrationSchema: [
			{ week: 1, durationWeeks: 4, dose: 2.5 },
			{ week: 5, durationWeeks: 4, dose: 5 },
			{ week: 9, durationWeeks: 4, dose: 7.5 },
			{ week: 13, durationWeeks: 4, dose: 10 },
			{ week: 17, durationWeeks: 4, dose: 12.5 },
			{ week: 21, durationWeeks: 4, dose: 15 },
		],
		recommendedSyringe: 0.5,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "tirzepatid",
		relatedPeptides: ["semaglutid", "retatrutide"],
		cas: "2023788-19-2",
		molecularFormula: "C₂₂₅H₃₄₈N₄₈O₆₈",
		molarMass: "4 813 g/mol",
		synonyms: ["LY3298176", "Mounjaro", "Zepbound"],
		studyReferences: [
			{
				author: "Frías et al.",
				journal: "NEJM",
				year: 2021,
				title: "Tirzepatide versus Semaglutide Once Weekly in Type 2 Diabetes",
				url: "https://pubmed.ncbi.nlm.nih.gov/34170647/",
			},
			{
				author: "Coskun et al.",
				journal: "Mol Metab",
				year: 2018,
				title: "LY3298176, a novel dual GIP and GLP-1 receptor agonist",
				url: "https://pubmed.ncbi.nlm.nih.gov/30473097/",
			},
			{
				author: "Jastreboff et al.",
				journal: "NEJM",
				year: 2022,
				title: "Tirzepatide Once Weekly for the Treatment of Obesity",
				url: "https://pubmed.ncbi.nlm.nih.gov/35658024/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "ghk-cu",
		name: {
			en: "GHK-Cu",
			de: "GHK-Cu",
			it: "GHK-Cu",
			es: "GHK-Cu",
			fr: "GHK-Cu",
			nl: "GHK-Cu",
			pt: "GHK-Cu",
			"pt-br": "GHK-Cu",
			pl: "GHK-Cu",
			sv: "GHK-Cu",
			da: "GHK-Cu",
			el: "GHK-Cu",
			cs: "GHK-Cu",
			fi: "GHK-Cu",
			ro: "GHK-Cu",
			hu: "GHK-Cu",
			sk: "GHK-Cu",
			tr: "GHK-Cu",
			nb: "GHK-Cu",
			bg: "GHK-Cu",
			hr: "GHK-Cu",
			sl: "GHK-Cu",
			sr: "GHK-Cu",
			bs: "GHK-Cu",
			mk: "GHK-Cu",
			lt: "GHK-Cu",
			lv: "GHK-Cu",
			et: "GHK-Cu",
		},
		category: {
			en: "Copper Peptide",
			de: "Kupferpeptid",
			it: "Peptide di rame",
			es: "Péptido de cobre",
			fr: "Peptide de cuivre",
			nl: "Koperpeptide",
			pt: "Péptido de cobre",
			"pt-br": "Peptídeo de cobre",
			pl: "Peptyd miedziowy",
			sv: "Kopparpeptid",
			da: "Kobberpeptid",
			el: "Πεπτίδιο χαλκού",
			cs: "Měďnatý peptid",
			fi: "Kuparipeptidi",
			ro: "Peptidă de cupru",
			hu: "Rézpeptid",
			sk: "Meďnatý peptid",
			tr: "Bakır peptidi",
			nb: "Kobberpeptid",
			bg: "Меден пептид",
			hr: "Bakreni peptid",
			sl: "Bakrov peptid",
			sr: "Bakarni peptid",
			bs: "Bakarni peptid",
			mk: "Бакарен пептид",
			lt: "Vario peptidas",
			lv: "Vara peptīds",
			et: "Vaskepeptiid",
		},
		defaultVialSizes: [10, 20, 50],
		defaultVialSize: 50,
		defaultBacWater: 5,
		defaultDose: 2,
		doseRange: { min: 1, max: 4 },
		doseUnit: "mg",
		dosePresets: [1, 2, 3, 4],
		// No published systemic PK study exists (source verification 2026-08-11).
		halfLife: "",
		frequency: "daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "ghk-cu",
		relatedPeptides: ["bpc-157", "glow-stack"],
		cas: "49557-75-7",
		molecularFormula: "C₁₄H₂₃CuN₆O₄⁺",
		molarMass: "403 g/mol",
		synonyms: ["Copper tripeptide-1", "Cu-GHK", "Prezatide copper acetate"],
		studyReferences: [
			{
				author: "Pickart & Margolina",
				journal: "Int J Mol Sci",
				year: 2018,
				title: "Regenerative and Protective Actions of the GHK-Cu Peptide",
				url: "https://pubmed.ncbi.nlm.nih.gov/29986520/",
			},
			{
				author: "Pickart et al.",
				journal: "Oxid Med Cell Longev",
				year: 2012,
				title: "GHK-Cu in Prevention of Oxidative Stress and Aging",
				url: "https://pubmed.ncbi.nlm.nih.gov/22666519/",
			},
			{
				author: "Pickart et al.",
				journal: "BioMed Res Int",
				year: 2015,
				title: "GHK Peptide Modulates Cellular Pathways in Skin Regeneration",
				url: "https://pubmed.ncbi.nlm.nih.gov/26236730/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "ahk-cu",
		name: {
			en: "AHK-Cu",
			de: "AHK-Cu",
			it: "AHK-Cu",
			es: "AHK-Cu",
			fr: "AHK-Cu",
			nl: "AHK-Cu",
			pt: "AHK-Cu",
			"pt-br": "AHK-Cu",
			pl: "AHK-Cu",
			sv: "AHK-Cu",
			da: "AHK-Cu",
			el: "AHK-Cu",
			cs: "AHK-Cu",
			fi: "AHK-Cu",
			ro: "AHK-Cu",
			hu: "AHK-Cu",
			sk: "AHK-Cu",
			tr: "AHK-Cu",
			nb: "AHK-Cu",
			bg: "AHK-Cu",
			hr: "AHK-Cu",
			sl: "AHK-Cu",
			sr: "AHK-Cu",
			bs: "AHK-Cu",
			mk: "AHK-Cu",
			lt: "AHK-Cu",
			lv: "AHK-Cu",
			et: "AHK-Cu",
		},
		category: {
			en: "Copper Peptide",
			de: "Kupferpeptid",
			it: "Peptide di rame",
			es: "Péptido de cobre",
			fr: "Peptide de cuivre",
			nl: "Koperpeptide",
			pt: "Péptido de cobre",
			"pt-br": "Peptídeo de cobre",
			pl: "Peptyd miedziowy",
			sv: "Kopparpeptid",
			da: "Kobberpeptid",
			el: "Πεπτίδιο χαλκού",
			cs: "Měďnatý peptid",
			fi: "Kuparipeptidi",
			ro: "Peptidă de cupru",
			hu: "Rézpeptid",
			sk: "Meďnatý peptid",
			tr: "Bakır peptidi",
			nb: "Kobberpeptid",
			bg: "Меден пептид",
			hr: "Bakreni peptid",
			sl: "Bakrov peptid",
			sr: "Bakarni peptid",
			bs: "Bakarni peptid",
			mk: "Бакарен пептид",
			lt: "Vario peptidas",
			lv: "Vara peptīds",
			et: "Vaskepeptiid",
		},
		calculatorMode: "research-dilution",
		defaultVialSizes: [100],
		defaultVialSize: 100,
		defaultBacWater: 5,
		defaultDose: 1,
		halfLife: "—",
		frequency: "—",
		recommendedSyringe: 1.0,
		hasBergdorfProduct: isFiveProductLaunchLive("ahk-cu"),
		bergdorfProductSlug: isFiveProductLaunchLive("ahk-cu") ? "ahk-cu" : undefined,
		relatedPeptides: [],
		// No cas/molecularFormula/molarMass: PubChem has no clean AHK-Cu record. The only hit
		// (CID 168431292, CAS 682809-81-0) is the monohydrochloride copper complex anion
		// (C15H24ClCuN6O4-), not the neutral AHK-Cu we sell, so the specs stay omitted.
		// "Copper tripeptide-3" is deliberately NOT listed as a synonym: the name exists only
		// in supplier catalogues (with mutually contradictory CAS numbers); CosIng has no entry
		// of that name, and the registered INCI name Tripeptide-3 denotes a different peptide
		// (glycine/serine/valine), so it is not a verified synonym of AHK-Cu.
		synonyms: ["Alanine-histidine-lysine copper complex"],
	},
	{
		slug: "bpc-157",
		name: {
			en: "BPC-157",
			de: "BPC-157",
			it: "BPC-157",
			es: "BPC-157",
			fr: "BPC-157",
			nl: "BPC-157",
			pt: "BPC-157",
			"pt-br": "BPC-157",
			pl: "BPC-157",
			sv: "BPC-157",
			da: "BPC-157",
			el: "BPC-157",
			cs: "BPC-157",
			fi: "BPC-157",
			ro: "BPC-157",
			hu: "BPC-157",
			sk: "BPC-157",
			tr: "BPC-157",
			nb: "BPC-157",
			bg: "BPC-157",
			hr: "BPC-157",
			sl: "BPC-157",
			sr: "BPC-157",
			bs: "BPC-157",
			mk: "BPC-157",
			lt: "BPC-157",
			lv: "BPC-157",
			et: "BPC-157",
		},
		category: {
			en: "Tissue Repair",
			de: "Gewebereparatur",
			it: "Riparazione dei tessuti",
			es: "Reparación tisular",
			fr: "Réparation tissulaire",
			nl: "Weefselherstel",
			pt: "Reparação tecidular",
			"pt-br": "Reparação tecidual",
			pl: "Naprawa tkanek",
			sv: "Vävnadsreparation",
			da: "Vævsreparation",
			el: "Επιδιόρθωση ιστών",
			cs: "Obnova tkání",
			fi: "Kudoskorjaus",
			ro: "Repararea țesuturilor",
			hu: "Szöveti helyreállítás",
			sk: "Obnova tkanív",
			tr: "Doku onarımı",
			nb: "Vevsreparasjon",
			bg: "Възстановяване на тъкани",
			hr: "Obnova tkiva",
			sl: "Obnova tkiva",
			sr: "Obnova tkiva",
			bs: "Obnova tkiva",
			mk: "Обнова на ткиво",
			lt: "Audinių atkūrimas",
			lv: "Audu atjaunošana",
			et: "Kudede taastamine",
		},
		defaultVialSizes: [2, 5, 10],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.25,
		doseRange: { min: 0.2, max: 0.5 },
		doseUnit: "mg",
		dosePresets: [0.2, 0.25, 0.3, 0.5],
		// He et al., Front Pharmacol 2022 (PMID 36588717): elimination t1/2 < 30 min
		// i.v. in rat/dog — the circulating multi-hour figures are unsupported. No
		// halfLifeHours on purpose: no half-life spoke for a sub-30-minute peptide.
		halfLife: "<30 minutes",
		halfLifeSource: "He et al., Front Pharmacol 2022 (rat/dog, i.v.)",
		frequency: "daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "bpc-157",
		relatedPeptides: ["tb-500", "ghk-cu", "kpv"],
		cas: "137525-51-0",
		molecularFormula: "C₆₂H₉₈N₁₆O₂₂",
		molarMass: "1 420 g/mol",
		synonyms: ["PL 14736", "PL-10", "Bepecin", "Pentadecapeptide BPC 157"],
		studyReferences: [
			{
				author: "Seiwerth et al.",
				journal: "Curr Pharm Des",
				year: 2018,
				title: "BPC 157 and Standard Angiogenic Growth Factors in Tract Healing",
				url: "https://pubmed.ncbi.nlm.nih.gov/29998800/",
			},
			{
				author: "Krivic et al.",
				journal: "Inflammation Research",
				year: 2008,
				title: "BPC 157 modulates Achilles tendon recovery after transection",
				url: "https://pubmed.ncbi.nlm.nih.gov/18594781/",
			},
			{
				author: "Park et al.",
				journal: "Curr Pharm Des",
				year: 2020,
				title: "BPC 157 Rescued NSAID-cytotoxicity Via Stabilizing Intestinal Permeability",
				url: "https://pubmed.ncbi.nlm.nih.gov/32445447/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "tb-500",
		name: {
			en: "TB-500",
			de: "TB-500",
			it: "TB-500",
			es: "TB-500",
			fr: "TB-500",
			nl: "TB-500",
			pt: "TB-500",
			"pt-br": "TB-500",
			pl: "TB-500",
			sv: "TB-500",
			da: "TB-500",
			el: "TB-500",
			cs: "TB-500",
			fi: "TB-500",
			ro: "TB-500",
			hu: "TB-500",
			sk: "TB-500",
			tr: "TB-500",
			nb: "TB-500",
			bg: "TB-500",
			hr: "TB-500",
			sl: "TB-500",
			sr: "TB-500",
			bs: "TB-500",
			mk: "TB-500",
			lt: "TB-500",
			lv: "TB-500",
			et: "TB-500",
		},
		category: {
			en: "Thymosin Beta-4",
			de: "Thymosin Beta-4",
			it: "Timosina beta-4",
			es: "Timosina beta-4",
			fr: "Thymosine bêta-4",
			nl: "Thymosine bèta-4",
			pt: "Timosina beta-4",
			"pt-br": "Timosina beta-4",
			pl: "Tymozyna beta-4",
			sv: "Tymosin beta-4",
			da: "Thymosin beta-4",
			el: "Θυμοσίνη βήτα-4",
			cs: "Thymosin beta-4",
			fi: "Tymosiini beeta-4",
			ro: "Timosină beta-4",
			hu: "Timozin-β4",
			sk: "Tymozín beta-4",
			tr: "Timozin beta-4",
			nb: "Thymosin beta-4",
			bg: "Тимозин β4",
			hr: "Timosin beta-4",
			sl: "Thymosin Beta-4",
			sr: "Thymosin Beta-4",
			bs: "Thymosin Beta-4",
			mk: "Тимозин бета-4 (Thymosin Beta-4)",
			lt: "Thymosin Beta-4",
			lv: "Thymosin Beta-4",
			et: "Thymosin Beta-4",
		},
		defaultVialSizes: [2, 5, 10],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 2.5,
		doseRange: { min: 2, max: 5 },
		doseUnit: "mg",
		dosePresets: [2, 2.5, 5],
		halfLife: "",
		frequency: "2-3x weekly",
		recommendedSyringe: 1.0,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "tb-500",
		relatedPeptides: ["bpc-157", "ghk-cu", "thymosin-alpha-1"],
		synonyms: ["Ac-LKKTETQ"],
		studyReferences: [
			{
				author: "Treadwell et al.",
				journal: "Ann N Y Acad Sci",
				year: 2012,
				title: "Regenerative peptide thymosin β4 accelerates dermal healing",
				url: "https://pubmed.ncbi.nlm.nih.gov/23050815/",
			},
			{
				author: "Smart et al.",
				journal: "Nature",
				year: 2011,
				title: "De novo cardiomyocytes from activated adult heart after injury",
				url: "https://pubmed.ncbi.nlm.nih.gov/21654746/",
			},
			{
				author: "Ho et al.",
				journal: "J Chromatogr A",
				year: 2012,
				title: "Doping control analysis of TB-500 in equine urine and plasma by LC-MS",
				url: "https://pubmed.ncbi.nlm.nih.gov/23084823/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "cjc-1295-no-dac",
		name: {
			en: "CJC-1295 (no DAC)",
			de: "CJC-1295 (ohne DAC)",
			it: "CJC-1295 (senza DAC)",
			es: "CJC-1295 (sin DAC)",
			fr: "CJC-1295 sans DAC",
			nl: "CJC-1295 (zonder DAC)",
			pt: "CJC-1295 (sem DAC)",
			"pt-br": "CJC-1295 (sem DAC)",
			pl: "CJC-1295 (bez DAC)",
			sv: "CJC-1295 (utan DAC)",
			da: "CJC-1295 (uden DAC)",
			el: "CJC-1295 (χωρίς DAC)",
			cs: "CJC-1295 (bez DAC)",
			fi: "CJC-1295 (ilman DAC:ia)",
			ro: "CJC-1295 (fără DAC)",
			hu: "CJC-1295 (DAC nélkül)",
			sk: "CJC-1295 (bez DAC)",
			tr: "CJC-1295 (DAC içermeyen)",
			nb: "CJC-1295 (uten DAC)",
			bg: "CJC-1295 (без DAC)",
			hr: "CJC-1295 (bez DAC)",
			sl: "CJC-1295 (brez DAC)",
			sr: "CJC-1295 (bez DAC)",
			bs: "CJC-1295 (bez DAC)",
			mk: "CJC-1295 (без DAC)",
			lt: "CJC-1295 (be DAC)",
			lv: "CJC-1295 (bez DAC)",
			et: "CJC-1295 (ilma DAC-ta)",
		},
		category: {
			en: "GHRH Analog",
			de: "GHRH-Analog",
			it: "Analogo del GHRH",
			es: "Análogo de GHRH",
			fr: "Analogue de la GHRH",
			nl: "GHRH-analoog",
			pt: "Análogo de GHRH",
			"pt-br": "Análogo de GHRH",
			pl: "Analog GHRH",
			sv: "GHRH-analog",
			da: "GHRH-analog",
			el: "Ανάλογο GHRH",
			cs: "Analog GHRH",
			fi: "GHRH-analogi",
			ro: "Analog GHRH",
			hu: "GHRH-analóg",
			sk: "Analóg GHRH",
			tr: "GHRH analoğu",
			nb: "GHRH-analog",
			bg: "GHRH аналог",
			hr: "GHRH analog",
			sl: "GHRH analog",
			sr: "GHRH analog",
			bs: "GHRH analog",
			mk: "GHRH аналог",
			lt: "GHRH analogas",
			lv: "GHRH analogs",
			et: "GHRH analoog",
		},
		defaultVialSizes: [2, 5, 10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.1,
		doseRange: { min: 0.1, max: 0.2 },
		doseUnit: "mcg",
		dosePresets: [0.1, 0.15, 0.2],
		// Teichman 2006 measured CJC-1295 WITH DAC; no PK source exists for the
		// DAC-free form (source verification 2026-08-11).
		halfLife: "",
		frequency: "1-3x daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "cjc-1295",
		relatedPeptides: ["ipamorelin", "sermorelin", "tesamorelin", "hexarelin", "ghrp-6", "ghrp-2"],
	},
	{
		slug: "ipamorelin",
		name: {
			en: "Ipamorelin",
			de: "Ipamorelin",
			it: "Ipamorelin",
			es: "Ipamorelin",
			fr: "Ipamoréline",
			nl: "Ipamoreline",
			pt: "Ipamorelina",
			"pt-br": "Ipamorelina",
			pl: "Ipamorelina",
			sv: "Ipamorelin",
			da: "Ipamorelin",
			el: "Ipamorelin",
			cs: "Ipamorelin",
			fi: "Ipamoreliini",
			ro: "Ipamorelin",
			hu: "Ipamorelin",
			sk: "Ipamorelín",
			tr: "Ipamorelin",
			nb: "Ipamorelin",
			bg: "Ipamorelin",
			hr: "Ipamorelin",
			sl: "Ipamorelin",
			sr: "Ipamorelin",
			bs: "Ipamorelin",
			mk: "Ипаморелин (Ipamorelin)",
			lt: "Ipamorelin",
			lv: "Ipamorelin",
			et: "Ipamorelin",
		},
		category: {
			en: "GHRP",
			de: "GHRP",
			it: "GHRP",
			es: "GHRP",
			fr: "GHRP",
			nl: "GHRP",
			pt: "GHRP",
			"pt-br": "GHRP",
			pl: "GHRP",
			sv: "GHRP",
			da: "GHRP",
			el: "GHRP",
			cs: "GHRP",
			fi: "GHRP",
			ro: "GHRP",
			hu: "GHRP",
			sk: "GHRP",
			tr: "GHRP",
			nb: "GHRP",
			bg: "GHRP",
			hr: "GHRP",
			sl: "GHRP",
			sr: "GHRP",
			bs: "GHRP",
			mk: "GHRP",
			lt: "GHRP",
			lv: "GHRP",
			et: "GHRP",
		},
		defaultVialSizes: [2, 5],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.2,
		doseRange: { min: 0.2, max: 0.3 },
		doseUnit: "mcg",
		dosePresets: [0.2, 0.25, 0.3],
		halfLife: "~2 hours",
		halfLifeHours: 2,
		halfLifeSource:
			"Gobburu et al., Pharm Res 1999: phase 1 intravenous pharmacokinetic study in healthy men, terminal half-life about two hours. This route-, population- and parameter-specific result is not a universal subcutaneous half-life or a measured value for a particular research product.",
		halfLifeSourceLocalized: {
			en: "Gobburu et al., Pharm Res 1999: phase 1 intravenous pharmacokinetic study in healthy men, terminal half-life about two hours. This route-, population- and parameter-specific result is not a universal subcutaneous half-life or a measured value for a particular research product.",
			de: "Gobburu et al., Pharm Res 1999: gesunde Männer, intravenöse Phase-1-PK-Untersuchung, terminale Peptidhalbwertszeit etwa 2 h. Die IV-Populationsreferenz ist keine allgemeine SC- oder Forschungsproduktkonstante.",
			it: "Gobburu et al., Pharm Res 1999: uomini sani, studio farmacocinetico di fase 1 per via endovenosa; emivita terminale di circa due ore. Non è un valore universale per somministrazione sottocutanea o prodotti di ricerca.",
			es: "Gobburu et al., Pharm Res 1999: estudio farmacocinético de fase 1 por vía intravenosa en hombres sanos; semivida terminal de aproximadamente dos horas. La vía y la población delimitan el valor, que no acredita un parámetro subcutáneo universal ni de otro material de investigación.",
			fr: "Gobburu et al., Pharm Res 1999 : étude pharmacocinétique intraveineuse de phase 1 chez des hommes sains, demi-vie terminale d'environ deux heures ; aucune valeur universelle sous-cutanée ou propre à un produit de recherche n'en découle.",
			nl: "Gobburu et al., Pharm Res 1999: gezonde mannen in een intraveneuze fase 1-PK-studie, terminale halfwaardetijd ongeveer twee uur; geen algemene subcutane waarde of vastgestelde halfwaardetijd van een onderzoeksproduct",
			pt: "Gobburu et al., Pharm Res 1999: estudo farmacocinético de fase 1 em homens saudáveis após administração intravenosa; semivida terminal de cerca de duas horas. Não é um valor universal subcutâneo ou de qualquer produto de investigação.",
			"pt-br":
				"Gobburu et al., Pharm Res 1999: estudo farmacocinético de fase 1 em homens saudáveis, por via intravenosa, com meia-vida terminal próxima de duas horas. Não é um valor universal para administração subcutânea ou para produto de pesquisa.",
			pl: "Gobburu et al., Pharm Res 1999: zdrowi mężczyźni, dożylne badanie farmakokinetyczne fazy 1, końcowy okres półtrwania około dwóch godzin. Parametr dotyczy tej drogi, populacji i mierzonego peptydu, nie uniwersalnej wartości podskórnej lub produktu badawczego.",
			sv: "Gobburu et al., Pharm Res 1999: terminal halveringstid omkring två timmar efter intravenös tillförsel i en fas 1-studie hos friska män. Administreringsväg, population och terminal mätparameter avgränsar referensen; den är inget universellt subkutant värde eller en forskningsproduktegenskap.",
			da: "Gobburu et al., Pharm Res 1999: fase 1-farmakokinetik hos raske mænd efter intravenøs indgift; terminal halveringstid omkring to timer. Angivelsen gælder den undersøgte vej, population og måleparameter og dokumenterer ikke en universel subkutan værdi eller egenskaber af forskningsmateriale.",
			el: "Gobburu et al., Pharm Res 1999: φαρμακοκινητική φάσης 1 μετά από ενδοφλέβια χορήγηση σε υγιείς άνδρες, τελικός χρόνος ημιζωής περίπου δύο ώρες. Αναφορά συγκεκριμένης οδού, πληθυσμού και παραμέτρου, όχι γενικός υποδόριος χρόνος ή τιμή ερευνητικού προϊόντος.",
			cs: "Gobburu et al., Pharm Res 1999: farmakokinetická studie fáze 1 po nitrožilním podání u zdravých mužů, terminální poločas přibližně dvě hodiny. Nejde o univerzální subkutánní hodnotu ani parametr libovolného výzkumného produktu.",
			fi: "Gobburu et al., Pharm Res 1999: terveiden miesten vaiheen 1 laskimonsisäinen farmakokinetiikkatutkimus, terminaalinen puoliintumisaika noin kaksi tuntia. Tämä ei ole yleinen ihonalaisen annon tai tutkimustuotteen arvo.",
			ro: "Gobburu et al., Pharm Res 1999: farmacocinetică intravenoasă de fază 1 la bărbați sănătoși, timp terminal de înjumătățire de circa două ore; nu este o valoare universală subcutanată sau a produselor de cercetare.",
			hu: "Gobburu et al., Pharm Res 1999: egészséges férfiak intravénás, 1. fázisú PK-vizsgálata; körülbelül kétórás terminális peptidfelezési idő. Nem univerzális bőr alatti vagy kutatásitermék-érték.",
			sk: "Gobburu et al., Pharm Res 1999: zdraví muži, intravenózne farmakokinetické skúšanie fázy 1, terminálny polčas približne dve hodiny. Hodnota sa nevzťahuje automaticky na podkožnú cestu či inú výskumnú formuláciu.",
			tr: "Gobburu et al., Pharm Res 1999: sağlıklı erkeklerde intravenöz faz 1 uygulama sonrasında terminal peptit yarı ömrü yaklaşık iki saat. Yol, katılımcı grubu ve terminal ölçüm parametresine bağlıdır; deri altı kullanımın veya araştırma ürününün evrensel değeri değildir.",
			nb: "Gobburu et al., Pharm Res 1999: intravenøs fase 1-studie hos friske menn, terminal peptidhalveringstid omtrent to timer. Dette er ikke dokumentert som en universell subkutan verdi eller egenskap ved et forskningsprodukt.",
			bg: "Gobburu et al., Pharm Res 1999: фармакокинетично проучване фаза 1 при здрави мъже след интравенозно въвеждане; терминален полуживот около 2 h. Популацията, пътят и измереният пептид ограничават референцията; тя не установява универсална подкожна или продуктова стойност.",
			hr: "Gobburu et al., Pharm Res 1999: intravensko PK-istraživanje faze 1 u zdravih muškaraca, terminalni poluvijek približno dva sata. To nije univerzalna potkožna ili istraživačko-proizvodna vrijednost.",
			sl: "Gobburu et al., Pharm Res 1999: intravenska farmakokinetična študija faze 1 pri zdravih moških, terminalni razpolovni čas približno dve uri. Rezultat je vezan na pot uporabe, populacijo in parameter; ni splošna podkožna ali raziskovalno-proizvodna vrednost.",
			sr: "Gobburu et al., Pharm Res 1999: intravenska PK-studija faze 1 kod zdravih muškaraca, terminalno poluvreme oko dva sata. Rezultat je vezan za put primene, populaciju i parametar; nije univerzalna potkožna ili istraživačko-proizvodna vrednost.",
			bs: "Gobburu et al., Pharm Res 1999: intravenska PK-studija faze 1 kod zdravih muškaraca, terminalno poluvrijeme oko dva sata. Rezultat je vezan za put primjene, populaciju i parametar; nije univerzalna potkožna ili istraživačko-proizvodna vrijednost.",
			mk: "Gobburu et al., Pharm Res 1999: интравенска PK-студија од фаза 1 кај здрави мажи, терминално полувреме околу два часа. Резултатот е врзан за патот на примена, популацијата и параметарот; не е универзална поткожна или истражувачко-производна вредност.",
			lt: "Gobburu et al., Pharm Res 1999: 1 fazės intraveninės farmakokinetikos tyrimas su sveikais vyrais, terminalinė pusėjimo trukmė apie dvi valandas. Šis rezultatas susietas su vartojimo keliu, populiacija ir parametru, todėl nėra universali po oda vartojama pusėjimo trukmė ar konkretaus tyrimo produkto išmatuota vertė.",
			lv: "Gobburu et al., Pharm Res 1999: 1. fāzes intravenozs farmakokinētikas pētījums veseliem vīriešiem, terminālais pussabrukšanas laiks aptuveni divas stundas. Šis rezultāts ir saistīts ar ievadīšanas ceļu, populāciju un parametru, tāpēc tas nav universāls subkutāns pussabrukšanas laiks vai konkrēta pētniecības produkta izmērīta vērtība.",
			et: "Gobburu et al., Pharm Res 1999: 1. faasi veenisisene farmakokineetika uuring tervetel meestel, terminaalne poolväärtusaeg umbes kaks tundi. See tulemus on seotud manustamisviisi, populatsiooni ja parameetriga, seega ei ole see üldine nahaalune poolväärtusaeg ega konkreetse uurimistoote mõõdetud väärtus.",
		},
		frequency: "2-3x daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "ipamorelin",
		relatedPeptides: ["cjc-1295-no-dac", "hexarelin", "ghrp-6", "ghrp-2"],
		cas: "170851-70-4",
		molecularFormula: "C₃₈H₄₉N₉O₅",
		molarMass: "712 g/mol",
		synonyms: ["NNC 26-0161"],
		studyReferences: [
			{
				author: "Raun et al.",
				journal: "Eur J Endocrinol",
				year: 1998,
				title: "Ipamorelin, the first selective growth hormone secretagogue",
				url: "https://pubmed.ncbi.nlm.nih.gov/9849822/",
			},
			{
				author: "Beck et al.",
				journal: "Int J Colorectal Dis",
				year: 2014,
				title:
					"Prospective, randomized, controlled, proof-of-concept study of the ghrelin mimetic ipamorelin for the management of postoperative ileus in bowel resection patients",
				url: "https://pubmed.ncbi.nlm.nih.gov/25331030/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "aod-9604",
		name: {
			en: "AOD-9604",
			de: "AOD-9604",
			it: "AOD-9604",
			es: "AOD-9604",
			fr: "AOD-9604",
			nl: "AOD-9604",
			pt: "AOD-9604",
			"pt-br": "AOD-9604",
			pl: "AOD-9604",
			sv: "AOD-9604",
			da: "AOD-9604",
			el: "AOD-9604",
			cs: "AOD-9604",
			fi: "AOD-9604",
			ro: "AOD-9604",
			hu: "AOD-9604",
			sk: "AOD-9604",
			tr: "AOD-9604",
			nb: "AOD-9604",
			bg: "AOD-9604",
			hr: "AOD-9604",
			sl: "AOD-9604",
			sr: "AOD-9604",
			bs: "AOD-9604",
			mk: "AOD-9604",
			lt: "AOD-9604",
			lv: "AOD-9604",
			et: "AOD-9604",
		},
		category: {
			en: "Fat Loss",
			de: "Fettstoffwechselforschung",
			it: "Riduzione del grasso",
			es: "Pérdida de grasa",
			fr: "Perte de graisse",
			nl: "Vetverlies",
			pt: "Perda de gordura",
			"pt-br": "Redução de gordura",
			pl: "Redukcja tkanki tłuszczowej",
			sv: "Fettminskning",
			da: "Fedttab",
			el: "Απώλεια λίπους",
			cs: "Úbytek tuku",
			fi: "Rasvan vähentäminen",
			ro: "Reducerea grăsimii",
			hu: "Zsírcsökkentés",
			sk: "Redukcia tuku",
			tr: "Yağ kaybı",
			nb: "Fettreduksjon",
			bg: "Изгаряне на мазнини",
			hr: "Gubitak masti",
			sl: "Izguba maščobe",
			sr: "Gubitak masti",
			bs: "Gubitak masti",
			mk: "Губење на маснотии",
			lt: "Riebalų apykaita",
			lv: "Tauku vielmaiņa",
			et: "Rasvade ainevahetus",
		},
		defaultVialSizes: [2, 5],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.3,
		doseRange: { min: 0.25, max: 0.5 },
		doseUnit: "mcg",
		dosePresets: [0.25, 0.3, 0.4, 0.5],
		halfLife: "",
		frequency: "daily",
		recommendedSyringe: 0.3,
		// Delisted: the Saleor product resolves to null in BOTH channels (verified
		// 2026-08-21), so /products/aod-9604 is a dead end. Calculator + half-life
		// surfaces stay; no buy gate may claim it.
		hasBergdorfProduct: false,
		relatedPeptides: ["cjc-1295-no-dac", "ipamorelin", "hgh-fragment-176-191", "5-amino-1mq"],
		cas: "221231-10-3",
		molecularFormula: "C₇₈H₁₂₃N₂₃O₂₃S₂",
		molarMass: "1 815 g/mol",
		synonyms: ["AOD9604", "HGH 176-191", "HGH Frag 176-191"],
		studyReferences: [
			{
				author: "Heffernan et al.",
				journal: "Endocrinology",
				year: 2001,
				title: "Effects of human GH and lipolytic fragment AOD9604 on lipid metabolism in obese mice",
				url: "https://pubmed.ncbi.nlm.nih.gov/11713213/",
			},
			{
				author: "Heffernan et al.",
				journal: "Int J Obes",
				year: 2001,
				title: "Increase of fat oxidation and weight loss in obese mice",
				url: "https://pubmed.ncbi.nlm.nih.gov/11673763/",
			},
			{
				author: "Ng et al.",
				journal: "Hormone Research",
				year: 2000,
				title: "Metabolic studies of synthetic lipolytic domain (AOD9604) of human growth hormone",
				url: "https://pubmed.ncbi.nlm.nih.gov/11146367/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "pt-141",
		name: {
			en: "PT-141",
			de: "PT-141",
			it: "PT-141",
			es: "PT-141",
			fr: "PT-141",
			nl: "PT-141",
			pt: "PT-141",
			"pt-br": "PT-141",
			pl: "PT-141",
			sv: "PT-141",
			da: "PT-141",
			el: "PT-141",
			cs: "PT-141",
			fi: "PT-141",
			ro: "PT-141",
			hu: "PT-141",
			sk: "PT-141",
			tr: "PT-141",
			nb: "PT-141",
			bg: "PT-141",
			hr: "PT-141",
			sl: "PT-141",
			sr: "PT-141",
			bs: "PT-141",
			mk: "PT-141",
			lt: "PT-141",
			lv: "PT-141",
			et: "PT-141",
		},
		category: {
			en: "Bremelanotide",
			de: "Bremelanotid",
			it: "Bremelanotide",
			es: "Bremelanotide",
			fr: "Brémélanotide",
			nl: "Bremelanotide",
			pt: "Bremelanotide",
			"pt-br": "Bremelanotida",
			pl: "Bremelanotyd",
			sv: "Bremelanotid",
			da: "Bremelanotide",
			el: "Bremelanotide",
			cs: "Bremelanotid",
			fi: "Bremelanotidi",
			ro: "Bremelanotide",
			hu: "Bremelanotide",
			sk: "Bremelanotide",
			tr: "Bremelanotide",
			nb: "Bremelanotide",
			bg: "Бремеланотид",
			hr: "Bremelanotid",
			sl: "Bremelanotide",
			sr: "Bremelanotide",
			bs: "Bremelanotide",
			mk: "Бремеланотид (Bremelanotide)",
			lt: "Bremelanotide",
			lv: "Bremelanotide",
			et: "Bremelanotide",
		},
		calculatorMode: "dose",
		defaultVialSizes: [2, 5, 10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 1,
		doseUnit: "mg",
		halfLife: "~2.7 hours",
		halfLifeHours: 2.7,
		halfLifeSource:
			"Vyleesi US Prescribing Information, 2019, section 12.3: formulated subcutaneous bremelanotide, mean terminal elimination half-life 2.7 hours. This is a named-medicine and route-specific pharmacokinetic reference, not evidence of equivalence to other PT-141 material.",
		halfLifeSourceLocalized: {
			en: "Vyleesi US Prescribing Information, 2019, section 12.3: formulated subcutaneous bremelanotide, mean terminal elimination half-life 2.7 hours. This is a named-medicine and route-specific pharmacokinetic reference, not evidence of equivalence to other PT-141 material.",
			de: "Vyleesi-US-Fachinformation 2019, Abschnitt 12.3: formuliertes subkutanes Bremelanotidarzneimittel, mittlere terminale Halbwertszeit 2,7 h. Präparat- und Routenreferenz, kein Beleg der Gleichwertigkeit anderen Materials.",
			it: "Vyleesi US Prescribing Information 2019, sezione 12.3: bremelanotide formulato per via sottocutanea; emivita terminale media 2,7 ore. Il dato riguarda quel preparato e quella via, senza dimostrare equivalenza di altri materiali.",
			es: "Vyleesi US Prescribing Information, 2019, apartado 12.3: bremelanotide formulado para vía subcutánea, semivida terminal media de 2,7 horas. Es referencia de ese preparado y vía, no prueba de equivalencia de otras sustancias o productos ni una pauta de uso humano.",
			fr: "Vyleesi, informations américaines de 2019, section 12.3 : brémélanotide pharmaceutique formulé sous-cutané, demi-vie terminale moyenne de 2,7 h ; référence de cette préparation et de cette voie, sans équivalence automatique avec un matériau de recherche.",
			nl: "Vyleesi US Prescribing Information, 2019, paragraaf 12.3: geformuleerd subcutaan bremelanotide, gemiddelde terminale halfwaardetijd 2,7 uur; de waarde betreft dit geneesmiddel en deze route",
			pt: "Vyleesi, informação norte-americana de 2019, secção 12.3: bremelanotida formulada para administração subcutânea, semivida terminal média de 2,7 horas. A referência pertence ao medicamento e à via estudados, sem estabelecer equivalência de outro material.",
			"pt-br":
				"Vyleesi US Prescribing Information, 2019, seção 12.3: preparação formulada de bremelanotida subcutânea, meia-vida terminal média de 2,7 horas. A medida pertence ao medicamento e à via descritos; não demonstra equivalência com outro material.",
			pl: "Vyleesi, amerykańska informacja o leku 2019, punkt 12.3: określony podskórny preparat bremelanotydu, średni końcowy okres półtrwania 2,7 godziny. To referencja leku i drogi podania, nie potwierdzenie równoważności innego materiału.",
			sv: "Vyleesi US Prescribing Information, 2019, avsnitt 12.3: formulerat subkutant bremelanotid, genomsnittlig terminal halveringstid 2,7 timmar. Preparat- och administreringsspecifik referens som inte ger användningsinstruktioner eller visar likvärdighet med annat material.",
			da: "Vyleesi, amerikansk produktinformation fra 2019, afsnit 12.3: formuleret subkutant bremelanotid med en gennemsnitlig terminal halveringstid på 2,7 timer. Præparat- og indgiftsvejsspecifik reference, ikke dokumentation for et andet materiales kliniske egenskaber.",
			el: "Vyleesi US Prescribing Information 2019, ενότητα 12.3: μέσος τελικός χρόνος ημιζωής 2,7 ώρες για το υποδόριο φαρμακευτικό σκεύασμα μπρεμελανοτίδης. Η αναφορά αφορά Vyleesi και τη συγκεκριμένη οδό, χωρίς να τεκμηριώνει ισοδυναμία άλλου υλικού.",
			cs: "Vyleesi US Prescribing Information, 2019, oddíl 12.3: formulovaný subkutánní bremelanotid, průměrný terminální poločas 2,7 hodiny. Údaj se vztahuje k přípravku a cestě podání, nikoli automaticky k jinému materiálu.",
			fi: "Vyleesin Yhdysvaltain valmisteinformaatio 2019, kohta 12.3: valmistettu ihonalainen bremelanotidilääke, keskimääräinen terminaalinen puoliintumisaika 2,7 tuntia. Valmiste ja antoreitti rajaavat viitettä; luku ei osoita muun tutkimusmateriaalin vastaavuutta.",
			ro: "Vyleesi, informația de prescriere SUA 2019, secțiunea 12.3: bremelanotidă formulată subcutanat, medie terminală de 2,7 ore; condițiile preparatului nu definesc automat alte materiale sau căi.",
			hu: "Vyleesi amerikai előírás, 2019, 12.3. szakasz: formulált bőr alatti bremelanotid; átlagos terminális felezési idő 2,7 óra. Készítmény- és útvonalfüggő adat, nem emberi alkalmazási utasítás vagy más anyag egyenértékűsége.",
			sk: "Vyleesi, americká informácia o lieku z roku 2019, časť 12.3: formulovaný podkožný bremelanotid, priemerný terminálny polčas 2,7 hodiny. Referencia prípravku a cesty nepotvrdzuje rovnocennosť iného materiálu.",
			tr: "Vyleesi ABD ürün bilgisi, 2019, bölüm 12.3: formüle edilmiş deri altı bremelanotidde ortalama terminal yarı ömür 2,7 saat. Belirli preparat ve yola aittir; uygulama talimatı veya başka materyalle eşdeğerlik kanıtı değildir.",
			nb: "Vyleesi US Prescribing Information, 2019, punkt 12.3: bremelanotid i formulert subkutant legemiddel, gjennomsnittlig terminal halveringstid 2,7 timer. Referansen gjelder dette preparatet og denne tilførselsveien.",
			bg: "Vyleesi, американска информация 2019, раздел 12.3: формулиран подкожен бремеланотид, среден терминален полуживот 2,7 h. Стойността принадлежи на този препарат и път; тя не доказва равностойност на изследователски материал.",
			hr: "Vyleesi, američke informacije o lijeku 2019., odjeljak 12.3: formulirani potkožni bremelanotid, prosječni terminalni poluvijek 2,7 sati. Pripravak i put dio su reference; ne potvrđuju jednakost drugog materijala.",
			sl: "Vyleesi, ameriške informacije o zdravilu 2019, razdelek 12.3: formulirani podkožni bremelanotid, povprečni terminalni razpolovni čas 2,7 ure. Pripravek in pot uporabe sta del reference; ne potrjujeta enakovrednosti drugega materiala.",
			sr: "Vyleesi, američke informacije o leku 2019, odeljak 12.3: formulisani potkožni bremelanotid, prosečno terminalno poluvreme 2,7 sati. Pripravak i put primene deo su reference; ne potvrđuju jednakost drugog materijala.",
			bs: "Vyleesi, američke informacije o lijeku 2019, odjeljak 12.3: formulirani potkožni bremelanotid, prosječno terminalno poluvrijeme 2,7 sati. Pripravak i put primjene dio su reference; ne potvrđuju jednakost drugog materijala.",
			mk: "Vyleesi, американски информации за лекот 2019, дел 12.3: формулиран поткожен бремеланотид, просечно терминално полувреме 2,7 часа. Препаратот и патот на примена се дел од референцата; не потврдуваат еднаквост на друг материјал.",
			lt: "Vyleesi, JAV informacija apie vaistą, 2019, 12.3 skirsnis: suformuluotas po oda vartojamas bremelanotidas, vidutinė terminalinė eliminacijos pusėjimo trukmė 2,7 valandos. Tai konkretaus vaisto ir vartojimo kelio farmakokinetikos nuoroda, o ne kitos PT-141 medžiagos lygiavertiškumo įrodymas.",
			lv: "Vyleesi, ASV zāļu informācija, 2019, 12.3. sadaļa: formulēts subkutāni lietojams bremelanotīds, vidējais terminālais eliminācijas pussabrukšanas laiks 2,7 stundas. Šī ir konkrēta medikamenta un ievadīšanas ceļa farmakokinētikas atsauce, nevis pierādījums par līdzvērtību citam PT-141 materiālam.",
			et: "Vyleesi, USA ravimiteave, 2019, jaotis 12.3: formuleeritud nahaalune bremelanotiid, keskmine terminaalne eliminatsiooni poolväärtusaeg 2,7 tundi. See on konkreetse ravimi ja manustamisviisi farmakokineetiline viide, mitte tõend samaväärsuse kohta teise PT-141 materjaliga.",
		},
		frequency: "—",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: isFiveProductLaunchLive("pt-141"),
		bergdorfProductSlug: isFiveProductLaunchLive("pt-141") ? "pt-141" : undefined,
		relatedPeptides: [],
		cas: "189691-06-3",
		molecularFormula: "C₅₀H₆₈N₁₄O₁₀",
		molarMass: "1 025 g/mol",
		synonyms: ["Bremelanotide"],
		studyReferences: [
			{
				author: "Kingsberg et al.",
				journal: "Obstet Gynecol",
				year: 2019,
				title:
					"Bremelanotide for the treatment of hypoactive sexual desire disorder: two randomized phase 3 trials (RECONNECT)",
				url: "https://pubmed.ncbi.nlm.nih.gov/31599840/",
			},
			{
				author: "Simon et al.",
				journal: "Obstet Gynecol",
				year: 2019,
				title: "Long-term safety and efficacy of bremelanotide for hypoactive sexual desire disorder",
				url: "https://pubmed.ncbi.nlm.nih.gov/31599847/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "mots-c",
		name: {
			en: "MOTS-c",
			de: "MOTS-c",
			it: "MOTS-c",
			es: "MOTS-c",
			fr: "MOTS-c",
			nl: "MOTS-c",
			pt: "MOTS-c",
			"pt-br": "MOTS-c",
			pl: "MOTS-c",
			sv: "MOTS-c",
			da: "MOTS-c",
			el: "MOTS-c",
			cs: "MOTS-c",
			fi: "MOTS-c",
			ro: "MOTS-c",
			hu: "MOTS-c",
			sk: "MOTS-c",
			tr: "MOTS-c",
			nb: "MOTS-c",
			bg: "MOTS-c",
			hr: "MOTS-c",
			sl: "MOTS-c",
			sr: "MOTS-c",
			bs: "MOTS-c",
			mk: "MOTS-c",
			lt: "MOTS-c",
			lv: "MOTS-c",
			et: "MOTS-c",
		},
		category: {
			en: "Mitochondrial",
			de: "Mitochondrial",
			it: "Mitocondriale",
			es: "Mitocondrial",
			fr: "Mitochondrial",
			nl: "Mitochondriaal",
			pt: "Mitocondrial",
			"pt-br": "Mitocondrial",
			pl: "Mitochondria",
			sv: "Mitokondriell",
			da: "Mitokondrier",
			el: "Μιτοχονδριακό",
			cs: "Mitochondriální",
			fi: "Mitokondriot",
			ro: "Mitocondrial",
			hu: "Mitokondriális",
			sk: "Mitochondriálne",
			tr: "Mitokondriyal",
			nb: "Mitokondriell",
			bg: "Митохондриален",
			hr: "Mitohondrijski",
			sl: "Mitohondrijski",
			sr: "Mitohondrijski",
			bs: "Mitohondrijski",
			mk: "Митохондриски",
			lt: "Mitochondrinis",
			lv: "Mitohondriāls",
			et: "Mitokondriaalne",
		},
		defaultVialSizes: [5, 10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 5,
		doseRange: { min: 5, max: 10 },
		doseUnit: "mg",
		dosePresets: [5, 7.5, 10],
		// Lee 2015 (Cell Metab) contains no pharmacokinetics; no PK source exists
		// (source verification 2026-08-11).
		halfLife: "",
		frequency: "weekly",
		recommendedSyringe: 1.0,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "mots-c",
		relatedPeptides: ["ss-31", "aod-9604", "epithalon", "5-amino-1mq"],
	},
	{
		slug: "selank",
		name: {
			en: "Selank",
			de: "Selank",
			it: "Selank",
			es: "Selank",
			fr: "Selank",
			nl: "Selank",
			pt: "Selank",
			"pt-br": "Selank",
			pl: "Selank",
			sv: "Selank",
			da: "Selank",
			el: "Selank",
			cs: "Selank",
			fi: "Selank",
			ro: "Selank",
			hu: "Selank",
			sk: "Selank",
			tr: "Selank",
			nb: "Selank",
			bg: "Селанк (Selank)",
			hr: "Selank",
			sl: "Selank",
			sr: "Selank",
			bs: "Selank",
			mk: "Селанк (Selank)",
			lt: "Selank",
			lv: "Selank",
			et: "Selank",
		},
		category: {
			en: "Anxiolytic",
			de: "Anxiolytisch",
			it: "Ansiolitico",
			es: "Ansiolítico",
			fr: "Anxiolytique",
			nl: "Anxiolytisch",
			pt: "Ansiolítico",
			"pt-br": "Ansiolítico",
			pl: "Anksjolityki",
			sv: "Anxiolytisk",
			da: "Anxiolytisk",
			el: "Αγχολυτικό",
			cs: "Anxiolytika",
			fi: "Anksiolyyttinen",
			ro: "Anxiolitic",
			hu: "Szorongásoldó",
			sk: "Anxiolytiká",
			tr: "Anksiyolitik",
			nb: "Angstdempende",
			bg: "Анксиолитик",
			hr: "Anksiolitik",
			sl: "Anksiolitik",
			sr: "Anksiolitik",
			bs: "Anksiolitik",
			mk: "Анксиолитик",
			lt: "Anksiolitinis",
			lv: "Anksiolītisks",
			et: "Anksiolüütiline",
		},
		// Saleor only sells 10 mg vials (1/2/3 pack), so the spoke defaults to the size we
		// ship — same reasoning as SS-31: a smaller default turns every buy-bridge match
		// into a permanent "larger vial" upsell (no per-dose anchor, never direct-add
		// eligible). 2 mg and 5 mg stay selectable calculation options in the chips.
		defaultVialSizes: [2, 5, 10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.25,
		doseRange: { min: 0.15, max: 0.5 },
		doseUnit: "mcg",
		dosePresets: [0.15, 0.25, 0.5],
		halfLife: "~minutes (intranasal)",
		frequency: "daily",
		recommendedSyringe: 0.3,
		// Live + purchasable in BOTH channels (verified against the prod API 2026-08-21);
		// the flag had been left at false, which was the reason the spoke shipped without
		// a hero buy card while the product was on sale.
		hasBergdorfProduct: true,
		bergdorfProductSlug: "selank",
		relatedPeptides: ["ss-31", "mots-c", "semax", "kpv", "dsip"],
		cas: "129954-34-3",
		molecularFormula: "C₃₃H₅₇N₁₁O₉",
		molarMass: "752 g/mol",
		synonyms: ["TKPRPGP", "Tuftsin analog", "TP-7", "Thr-Lys-Pro-Arg-Pro-Gly-Pro"],
		studyReferences: [
			{
				author: "Vyunova et al.",
				journal: "Protein Pept Lett",
				year: 2018,
				title: "Peptide-based Anxiolytics: Heptapeptide Selank Biological Activity",
				url: "https://pubmed.ncbi.nlm.nih.gov/30255741/",
			},
			{
				author: "Kasian et al.",
				journal: "Behav Neurol",
				year: 2017,
				title: "Selank enhances diazepam in reducing anxiety in chronic mild stress",
				url: "https://pubmed.ncbi.nlm.nih.gov/28280289/",
			},
			{
				author: "Kolik et al.",
				journal: "Bull Exp Biol Med",
				year: 2014,
				title: "Efficacy of Peptide Anxiolytic Selank in alcohol withdrawal",
				url: "https://pubmed.ncbi.nlm.nih.gov/24913576/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "ss-31",
		name: {
			en: "SS-31",
			de: "SS-31",
			it: "SS-31",
			es: "SS-31",
			fr: "SS-31",
			nl: "SS-31",
			pt: "SS-31",
			"pt-br": "SS-31",
			pl: "SS-31",
			sv: "SS-31",
			da: "SS-31",
			el: "SS-31",
			cs: "SS-31",
			fi: "SS-31",
			ro: "SS-31",
			hu: "SS-31",
			sk: "SS-31",
			tr: "SS-31",
			nb: "SS-31",
			bg: "SS-31",
			hr: "SS-31",
			sl: "SS-31",
			sr: "SS-31",
			bs: "SS-31",
			mk: "SS-31",
			lt: "SS-31",
			lv: "SS-31",
			et: "SS-31",
		},
		category: {
			en: "Elamipretide",
			de: "Elamipretid",
			it: "Elamipretide",
			es: "Elamipretide",
			fr: "Élamiprétide",
			nl: "Elamipretide",
			pt: "Elamipretida",
			"pt-br": "Elamipretida",
			pl: "Elamipretyd",
			sv: "Elamipretid",
			da: "Elamipretid",
			el: "Elamipretide",
			cs: "Elamipretid",
			fi: "Elamipretidi",
			ro: "Elamipretide",
			hu: "Elamipretid",
			sk: "Elamipretid",
			tr: "Elamipretid",
			nb: "Elamipretid",
			bg: "Еламипретид",
			hr: "Elamipretid",
			sl: "Elamipretide",
			sr: "Elamipretide",
			bs: "Elamipretide",
			mk: "Еламипретид (Elamipretide)",
			lt: "Elamipretide",
			lv: "Elamipretide",
			et: "Elamipretide",
		},
		defaultVialSizes: [5, 10],
		// Saleor only sells 10 mg vials (1/2/3 pack), so the spoke defaults to the size we
		// ship: a 5 mg default made every buy-bridge match a permanent "larger vial" upsell
		// (no per-dose anchor, no doses spec, never direct-add eligible). 5 mg stays a
		// selectable calculation option in the chips.
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.5,
		doseRange: { min: 0.1, max: 1 },
		doseUnit: "mg",
		dosePresets: [0.1, 0.25, 0.5, 1],
		halfLife: "~4 hours",
		halfLifeHours: 4,
		halfLifeSource:
			"FDA FORZINITY Integrated Review, 2025, Table 103: healthy subjects after intravenous infusion, six participants per cohort, cohort mean half-lives 3.23 to 3.93 hours. About four hours is a rounded IV study reference, supported by the FDA-reviewed original data rather than the Szeto review. It is not a universal SS-31 formulation or route value.",
		halfLifeSourceLocalized: {
			en: "FDA FORZINITY Integrated Review, 2025, Table 103: healthy subjects after intravenous infusion, six participants per cohort, cohort mean half-lives 3.23 to 3.93 hours. About four hours is a rounded IV study reference, supported by the FDA-reviewed original data rather than the Szeto review. It is not a universal SS-31 formulation or route value.",
			de: "FDA FORZINITY Integrated Review 2025, Tabelle 103: gesunde Probanden nach IV-Infusion, sechs je Kohorte; mittlere Halbwertszeiten 3,23–3,93 h. Rund 4 h ist eine gerundete IV-Studienreferenz, keine universelle SS-31-Formulierungs- oder Routenkonstante.",
			it: "FDA FORZINITY Integrated Review 2025, tabella 103: soggetti sani dopo infusione endovenosa, sei per coorte; emivite medie 3,23-3,93 ore. Circa quattro ore è un riferimento IV arrotondato. La rassegna Szeto non è la fonte primaria di queste misure e il dato non è universale per ogni formulazione o via.",
			es: "FDA, FORZINITY Integrated Review 2025, tabla 103: sujetos sanos tras infusión intravenosa, seis participantes por cohorte; medias de semivida de 3,23 a 3,93 horas. Cuatro horas es una referencia intravenosa redondeada. La revisión de Szeto no es la fuente original de esos datos ni la cifra es universal para material de investigación.",
			fr: "FDA, FORZINITY Integrated Review 2025, tableau 103 : volontaires sains après perfusion intraveineuse, six par cohorte, demi-vies moyennes de 3,23 à 3,93 h ; environ quatre heures est une référence arrondie de ces données originales, pas une constante de toute formulation.",
			nl: "FDA FORZINITY Integrated Review, 2025, tabel 103: gezonde proefpersonen na intraveneuze infusie, zes per cohort; gemiddelde halfwaardetijden 3,23 tot 3,93 uur; ongeveer vier uur is een afgeronde IV-studiereferentie, geen algemene waarde voor elke formulering of route",
			pt: "FDA, avaliação integrada de FORZINITY, 2025, tabela 103: voluntários saudáveis após perfusão intravenosa, seis por coorte, médias de 3,23 a 3,93 horas. Quatro horas é uma referência IV arredondada; não uma constante geral de SS-31 ou um valor primário proveniente da revisão de Szeto.",
			"pt-br":
				"FDA, FORZINITY Integrated Review, 2025, tabela 103: infusão intravenosa em pessoas saudáveis, seis participantes por grupo; meias-vidas médias de 3,23 a 3,93 horas. Quatro horas é uma referência intravenosa arredondada, não uma constante geral de SS-31. Os dados são da análise original da FDA, não uma medição humana do artigo de revisão de Szeto de 2014.",
			pl: "FDA FORZINITY Integrated Review 2025, tabela 103: zdrowi uczestnicy po infuzji dożylnej, po sześć osób w kohorcie, średnie okresy półtrwania 3,23–3,93 godziny. Cztery godziny są zaokrągloną referencją dożylną. Przegląd Szeto nie jest pierwotnym badaniem tych danych, a informacja FORZINITY nie ustala uniwersalnej wartości materiału.",
			sv: "FDA FORZINITY Integrated Review, 2025, tabell 103: friska försökspersoner efter intravenös infusion, sex deltagare per kohort, medelhalveringstider 3,23 till 3,93 timmar. Fyra timmar är en avrundad intravenös studiereferens från dessa primärdata, inte en universell materialsiffra eller ett primärfynd i Szetos översikt.",
			da: "FDA FORZINITY Integrated Review 2025, tabel 103: raske forsøgspersoner efter intravenøs infusion, seks i hver kohorte; middelhalveringstider 3,23 til 3,93 timer. Fire timer er en afrundet intravenøs forsøgsreference. Szeto-oversigten er ikke den originale humane PK-kilde, og tallet er ikke en universel materialekonstant.",
			el: "FDA FORZINITY Integrated Review 2025, πίνακας 103: υγιείς εθελοντές, ενδοφλέβια έγχυση, έξι άτομα ανά ομάδα και μέσοι χρόνοι ημιζωής 3,23 έως 3,93 ώρες. Οι τέσσερις ώρες είναι στρογγυλοποιημένη ενδοφλέβια αναφορά. Η ανασκόπηση Szeto δεν είναι η πρωτογενής φαρμακοκινητική πηγή· η τιμή δεν αποτελεί καθολικό χαρακτηριστικό κάθε υλικού SS-31.",
			cs: "FDA FORZINITY Integrated Review 2025, tabulka 103: zdravé osoby po nitrožilní infuzi, šest účastníků v každé skupině; průměrné poločasy 3,23 až 3,93 hodiny. Přibližně čtyři hodiny jsou zaokrouhlenou IV studijní referencí. Přehled Szeto 2014 není původní lidská PK studie a lékové informace neurčují univerzální hodnotu materiálu.",
			fi: "FDA, FORZINITY Integrated Review 2025, taulukko 103: terveet tutkittavat laskimoinfuusion jälkeen, kuusi henkilöä ryhmää kohti, keskimääräiset puoliintumisajat 3,23–3,93 tuntia. Noin neljä tuntia on pyöristetty laskimonsisäinen tutkimusviite. Lähde on FDA:n alkuperäisaineisto, ei Szeton katsauksen oma ihmisfarmakokinetiikkatutkimus.",
			ro: "FDA, evaluarea integrată FORZINITY 2025, tabelul 103: perfuzie intravenoasă la persoane sănătoase, șase participanți pe cohortă, medii de 3,23-3,93 ore. Patru ore este rotunjirea acestei referințe intravenoase, nu o constantă a tuturor formulărilor.",
			hu: "FDA FORZINITY Integrated Review, 2025, 103. táblázat: egészséges önkéntesek, intravénás infúzió, kohorszonként n = 6; átlagos felezési idők 3,23–3,93 óra. A négy óra kerekített IV-referencia, nem univerzális készítménykonstans. Az eredeti adat az FDA-értékelésből származik; Szeto munkája másodlagos áttekintés.",
			sk: "FDA, FORZINITY Integrated Review 2025, tabuľka 103: zdraví účastníci po intravenóznej infúzii, šesť osôb v každej kohorte; priemerné polčasy 3,23 až 3,93 hodiny. Približne štyri hodiny sú zaokrúhlenou intravenóznou referenciou, nie univerzálnou hodnotou každej formulácie.",
			tr: "FDA FORZINITY Integrated Review 2025, Tablo 103: sağlıklı kişilerin her biri altı katılımcılı intravenöz infüzyon kohortlarında ortalama yarı ömürler 3,23–3,93 saat. Dört saat yuvarlatılmış IV referansıdır; her formülasyon ve yol için sabit değildir. Özgün FDA verileri ikincil Szeto derlemesinden ayrılmalıdır.",
			nb: "FDA FORZINITY Integrated Review, 2025, tabell 103: friske frivillige etter intravenøs infusjon, seks per kohort; gjennomsnittlige halveringstider 3,23–3,93 timer. Fire timer er en avrundet IV-studiereferanse, ikke en generell verdi for alle formuleringer.",
			bg: "FDA, интегриран преглед за FORZINITY, 2025, таблица 103: здрави участници след интравенозна инфузия, по шестима в кохорта; средни полуживоти 3,23-3,93 h. Около 4 h е закръглена интравенозна референция, не универсална материална стойност. Szeto 2014 е вторичен обзор, не първично човешко PK измерване.",
			hr: "FDA, FORZINITY Integrated Review 2025, tablica 103: zdravi dobrovoljci nakon intravenske infuzije, šest po skupini, prosječni poluvijekovi 3,23 do 3,93 sata. Četiri sata zaokružena je intravenska referenca izvornih podataka, ne univerzalni broj ili izvorno mjerenje Szetoova pregleda.",
			sl: "FDA, FORZINITY Integrated Review 2025, razpredelnica 103: zdravi prostovoljci po intravenski infuziji, po šest v skupini; povprečni razpolovni časi 3,23 do 3,93 ure. Štiri ure so zaokrožena intravenska referenca izvirnih podatkov, ne univerzalna vrednost materiala ali izvirna meritev Szetojevega pregleda.",
			sr: "FDA, FORZINITY Integrated Review 2025, tabela 103: zdravi dobrovoljci nakon intravenske infuzije, po šest u kohorti; prosečna poluvremena 3,23 do 3,93 sata. Četiri sata su zaokružena intravenska referenca izvornih podataka, ne univerzalna vrednost materijala ili izvorno merenje Szetoovog pregleda.",
			bs: "FDA, FORZINITY Integrated Review 2025, tabela 103: zdravi dobrovoljci nakon intravenske infuzije, po šest u kohorti; prosječna poluvremena 3,23 do 3,93 sata. Četiri sata zaokružena su intravenska referenca izvornih podataka, ne univerzalna vrijednost materijala ili izvorno mjerenje Szetoovog pregleda.",
			mk: "FDA, FORZINITY Integrated Review 2025, табела 103: здрави доброволци по интравенска инфузија, по шест во кохорта; просечни полувремиња 3,23 до 3,93 часа. Четири часа е заокружена интравенска референца од изворните податоци, не универзална вредност на материјалот или изворно мерење од прегледот на Szeto.",
			lt: "FDA FORZINITY integruota peržiūra, 2025, 103 lentelė: sveiki asmenys po intraveninės infuzijos, po šešis dalyvius kohortoje; kohortų vidutinės pusėjimo trukmės 3,23 iki 3,93 valandos. Apie keturias valandas yra suapvalinta IV tyrimo nuoroda, paremta FDA peržiūrėtais pirminiais duomenimis, o ne Szeto apžvalga. Tai nėra universali SS-31 formulės ar vartojimo kelio vertė.",
			lv: "FDA FORZINITY apvienotais pārskats, 2025, 103. tabula: veseli cilvēki pēc intravenozas infūzijas, seši dalībnieki katrā kohortā; kohortu vidējie pussabrukšanas laiki 3,23 līdz 3,93 stundas. Apmēram četras stundas ir noapaļota IV pētījuma atsauce, kas balstīta uz FDA pārskatītiem oriģinālajiem datiem, nevis Szeto pārskatu. Tas nav universāls SS-31 preparāta vai ievadīšanas ceļa lielums.",
			et: "FDA FORZINITY koondülevaade, 2025, tabel 103: terved isikud pärast veenisisest infusiooni, kuus osalejat kohordi kohta; kohortide keskmised poolväärtusajad 3,23 kuni 3,93 tundi. Ligikaudu neli tundi on ümardatud IV-uuringu viide, mis tugineb FDA üle vaadatud algandmetele, mitte Szeto ülevaatele. See ei ole üldine SS-31 preparaadi ega manustamisviisi väärtus.",
		},
		frequency: "daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "ss-31",
		relatedPeptides: ["mots-c", "selank", "epithalon", "5-amino-1mq"],
		cas: "736992-21-5",
		molecularFormula: "C₃₂H₄₉N₉O₅",
		molarMass: "640 g/mol",
		synonyms: ["Elamipretide", "MTP-131", "Bendavia"],
		studyReferences: [
			{
				author: "Szeto et al.",
				journal: "Br J Pharmacol",
				year: 2014,
				title:
					"First-in-class cardiolipin-protective compound as a therapeutic agent to restore mitochondrial bioenergetics",
				url: "https://pubmed.ncbi.nlm.nih.gov/24117165/",
			},
			{
				author: "Karaa et al.",
				journal: "Neurology",
				year: 2018,
				title:
					"Randomized dose-escalation trial of elamipretide in adults with primary mitochondrial myopathy",
				url: "https://pubmed.ncbi.nlm.nih.gov/29500292/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "glow-stack",
		name: {
			de: "Glow Stack",
			en: "Glow Stack",
			bg: "Glow Stack",
			hr: "Glow Stack",
			sl: "Glow Stack",
			sr: "Glow Stack",
			bs: "Glow Stack",
			mk: "Glow Stack",
			lt: "Glow Stack",
			lv: "Glow Stack",
			et: "Glow Stack",
		},
		category: {
			de: "Stack",
			en: "Stack",
			bg: "Stack",
			hr: "Stack",
			sl: "Stack",
			sr: "Stack",
			bs: "Stack",
			mk: "Stack",
			lt: "Stack",
			lv: "Stack",
			et: "Stack",
		},
		// Single blend vial; total active mass = sum of components (50+10+10 = 70 mg). Prior [5,10]/10 were placeholder values copied from single-peptide entries.
		defaultVialSizes: [70],
		defaultVialSize: 70,
		defaultBacWater: 3,
		defaultDose: 1,
		doseRange: { min: 0.5, max: 2 },
		doseUnit: "mg",
		// Presets within the documented per-injection doseRange (blend total dose per injection).
		dosePresets: [0.5, 1, 1.5, 2],
		// Source: scripts/translator-input/products.json product description "Glow (GHK-Cu 50mg, TB500 10mg, BPC157 10mg)"
		components: [
			{ name: "GHK-Cu", mg: 50, anchor: true },
			{ name: "TB-500", mg: 10 },
			{ name: "BPC-157", mg: 10 },
		],
		halfLife: "varies",
		frequency: "daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "glow-stack",
		relatedPeptides: ["ghk-cu", "bpc-157", "klow-stack"],
	},
	{
		slug: "klow-stack",
		name: {
			de: "Klow Stack",
			en: "Klow Stack",
			bg: "Klow Stack",
			hr: "Klow Stack",
			sl: "Klow Stack",
			sr: "Klow Stack",
			bs: "Klow Stack",
			mk: "Klow Stack",
			lt: "Klow Stack",
			lv: "Klow Stack",
			et: "Klow Stack",
		},
		category: {
			de: "Stack",
			en: "Stack",
			bg: "Stack",
			hr: "Stack",
			sl: "Stack",
			sr: "Stack",
			bs: "Stack",
			mk: "Stack",
			lt: "Stack",
			lv: "Stack",
			et: "Stack",
		},
		// Single blend vial; total active mass = sum of components (25+10+10+10 = 55 mg). Prior [5,10]/10 were placeholder values copied from single-peptide entries.
		defaultVialSizes: [55],
		defaultVialSize: 55,
		defaultBacWater: 3,
		defaultDose: 1,
		doseRange: { min: 0.5, max: 2 },
		doseUnit: "mg",
		// Presets within the documented per-injection doseRange (blend total dose per injection).
		dosePresets: [0.5, 1, 1.5, 2],
		// Source: docs/staging/2026-06-05-cjc-klow/klow.product-payload.json
		// "GHK-Cu 25mg + TB-500 10mg + BPC-157 10mg + KPV 10mg per vial (55 mg total)"
		// Note: an 80mg variant with GHK-Cu 50mg exists per the guide; we use the documented 55mg primary vial here.
		components: [
			{ name: "GHK-Cu", mg: 25, anchor: true },
			{ name: "TB-500", mg: 10 },
			{ name: "BPC-157", mg: 10 },
			{ name: "KPV", mg: 10 },
		],
		halfLife: "varies",
		frequency: "daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "klow-stack",
		relatedPeptides: ["glow-stack", "ghk-cu", "bpc-157"],
	},
	{
		slug: "melanotan-2",
		name: {
			en: "Melanotan II",
			de: "Melanotan II",
			it: "Melanotan II",
			es: "Melanotan II",
			fr: "Melanotan II",
			nl: "Melanotan II",
			pt: "Melanotan II",
			"pt-br": "Melanotan II",
			pl: "Melanotan II",
			sv: "Melanotan II",
			da: "Melanotan II",
			el: "Melanotan II",
			cs: "Melanotan II",
			fi: "Melanotan II",
			ro: "Melanotan II",
			hu: "Melanotan II",
			sk: "Melanotan II",
			tr: "Melanotan II",
			nb: "Melanotan II",
			bg: "Melanotan II",
			hr: "Melanotan II",
			sl: "Melanotan II",
			sr: "Melanotan II",
			bs: "Melanotan II",
			mk: "Меланотан II (Melanotan II)",
			lt: "Melanotan II",
			lv: "Melanotan II",
			et: "Melanotan II",
		},
		category: {
			en: "Melanocortin",
			de: "Melanokortin",
			it: "Melanocortina",
			es: "Melanocortina",
			fr: "Mélanocortine",
			nl: "Melanocortine",
			pt: "Melanocortina",
			"pt-br": "Melanocortina",
			pl: "Melanokortyna",
			sv: "Melanokortin",
			da: "Melanokortin",
			el: "Μελανοκορτίνη",
			cs: "Melanokortin",
			fi: "Melanokortiini",
			ro: "Melanocortină",
			hu: "Melanokortin",
			sk: "Melanokortín",
			tr: "Melanokortin",
			nb: "Melanokortin",
			bg: "Меланокортин",
			hr: "Melanokortin",
			sl: "Melanokortin",
			sr: "Melanokortin",
			bs: "Melanokortin",
			mk: "Меланокортин",
			lt: "Melanokortinas",
			lv: "Melanokortīns",
			et: "Melanokortiin",
		},
		defaultVialSizes: [5, 10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.25,
		doseRange: { min: 0.1, max: 1 },
		doseUnit: "mcg",
		dosePresets: [0.1, 0.25, 0.5, 1],
		// No verifiable systemic PK citation (source verification 2026-08-11).
		halfLife: "",
		frequency: "2–3x weekly",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "mt-2",
		relatedPeptides: ["melanotan-1", "pt-141"],
		cas: "121062-08-6",
		molecularFormula: "C₅₀H₆₉N₁₅O₉",
		molarMass: "1 024 g/mol",
		synonyms: ["MT-II", "MT-2", "MTII"],
		studyReferences: [
			{
				author: "Dorr et al.",
				journal: "Life Sciences",
				year: 1996,
				title: "Evaluation of melanotan-II superpotent cyclic melanotropic peptide Phase I",
				url: "https://pubmed.ncbi.nlm.nih.gov/8637402/",
			},
			{
				author: "Wessells et al.",
				journal: "Urology",
				year: 2000,
				title: "Effect of alpha-MSH analog on penile erection and sexual desire in organic ED",
				url: "https://pubmed.ncbi.nlm.nih.gov/11018622/",
			},
			{
				author: "Hadley & Dorr",
				journal: "Peptides",
				year: 2006,
				title: "Melanocortin peptide therapeutics: historical milestones and clinical studies",
				url: "https://pubmed.ncbi.nlm.nih.gov/16412534/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "melanotan-1",
		name: {
			en: "Melanotan I",
			de: "Melanotan I",
			it: "Melanotan I",
			es: "Melanotan I",
			fr: "Melanotan I",
			nl: "Melanotan I",
			pt: "Melanotan I",
			"pt-br": "Melanotan I",
			pl: "Melanotan I",
			sv: "Melanotan I",
			da: "Melanotan I",
			el: "Melanotan I",
			cs: "Melanotan I",
			fi: "Melanotan I",
			ro: "Melanotan I",
			hu: "Melanotan I",
			sk: "Melanotan I",
			tr: "Melanotan I",
			nb: "Melanotan I",
			bg: "Melanotan I",
			hr: "Melanotan I",
			sl: "Melanotan I",
			sr: "Melanotan I",
			bs: "Melanotan I",
			mk: "Меланотан I (Melanotan I)",
			lt: "Melanotan I",
			lv: "Melanotan I",
			et: "Melanotan I",
		},
		category: {
			en: "Melanocortin",
			de: "Melanokortin",
			it: "Melanocortina",
			es: "Melanocortina",
			fr: "Mélanocortine",
			nl: "Melanocortine",
			pt: "Melanocortina",
			"pt-br": "Melanocortina",
			pl: "Melanokortyna",
			sv: "Melanokortin",
			da: "Melanokortin",
			el: "Μελανοκορτίνη",
			cs: "Melanokortin",
			fi: "Melanokortiini",
			ro: "Melanocortină",
			hu: "Melanokortin",
			sk: "Melanokortín",
			tr: "Melanokortin",
			nb: "Melanokortin",
			bg: "Меланокортин",
			hr: "Melanokortin",
			sl: "Melanokortin",
			sr: "Melanokortin",
			bs: "Melanokortin",
			mk: "Меланокортин",
			lt: "Melanokortinas",
			lv: "Melanokortīns",
			et: "Melanokortiin",
		},
		calculatorMode: "dose",
		defaultVialSizes: [5, 10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.25,
		doseUnit: "mg",
		halfLife: "~1 hour",
		halfLifeHours: 1,
		halfLifeSource:
			"Ugwu et al., Biopharm Drug Dispos 1997: subcutaneous solution studied in three men, beta-phase half-lives 0.8 to 1.7 hours. About one hour is a rounded reference from that small solution study, not an implant-release parameter or a pharmacokinetic value for the approved EPP medicine.",
		halfLifeSourceLocalized: {
			en: "Ugwu et al., Biopharm Drug Dispos 1997: subcutaneous solution studied in three men, beta-phase half-lives 0.8 to 1.7 hours. About one hour is a rounded reference from that small solution study, not an implant-release parameter or a pharmacokinetic value for the approved EPP medicine.",
			de: "Ugwu et al., Biopharm Drug Dispos 1997: SC-Lösung bei drei Männern, Beta-Phasen-Halbwertszeiten 0,8–1,7 h. Rund 1 h ist eine gerundete kleine Studienreferenz, keine PK-Angabe zur Freisetzung aus einem Scenesse-Implantat.",
			it: "Ugwu et al., Biopharm Drug Dispos 1997: soluzione sottocutanea in tre uomini, emivita della fase beta 0,8-1,7 ore; circa un'ora come piccolo riferimento sperimentale arrotondato. Non equivale al rilascio dell'impianto o alle proprietà del medicinale per EPP.",
			es: "Ugwu et al., Biopharm Drug Dispos 1997: solución subcutánea en tres hombres, semividas de fase beta de 0,8 a 1,7 horas. Una hora es una referencia redondeada de ese pequeño estudio, distinta de liberación de un implante y de la formulación farmacéutica para EPP.",
			fr: "Ugwu et al., Biopharm Drug Dispos 1997 : solution sous-cutanée chez trois hommes, demi-vies de phase bêta de 0,8 à 1,7 h ; environ une heure est une référence arrondie de cette petite étude, distincte de la libération de l'implant Scenesse dans l'EPP.",
			nl: "Ugwu et al., Biopharm Drug Dispos 1997: subcutane oplossing bij drie mannen, bètafasehalfwaardetijd 0,8 tot 1,7 uur; ongeveer één uur is een afgeronde kleine-studiereferentie, geen vrijgiftewaarde van het afamelanotide-implantaat bij EPP",
			pt: "Ugwu et al., Biopharm Drug Dispos 1997: solução subcutânea em três homens, semivida da fase beta de 0,8 a 1,7 horas. Cerca de uma hora é uma referência arredondada desse pequeno estudo, sem equivalência com libertação do implante ou medicamento de EPP.",
			"pt-br":
				"Ugwu et al., Biopharm Drug Dispos 1997: solução subcutânea em três homens, meia-vida da fase beta de 0,8 a 1,7 hora. Cerca de uma hora é uma referência arredondada desse pequeno estudo, não a liberação do implante Scenesse nem um parâmetro geral do medicamento para protoporfiria eritropoiética.",
			pl: "Ugwu et al., Biopharm Drug Dispos 1997: roztwór podskórny u trzech mężczyzn, okres półtrwania fazy beta 0,8–1,7 godziny. Około jednej godziny to zaokrąglona referencja małego badania roztworu, nie kinetyka uwalniania implantu Scenesse w EPP.",
			sv: "Ugwu et al., Biopharm Drug Dispos 1997: subkutan lösning hos tre män, halveringstid i betafasen 0,8 till 1,7 timmar. Omkring en timme är en avrundad referens från den lilla lösningsstudien, inte implantatets frisättning eller ett allmänt värde för EPP-läkemedlet.",
			da: "Ugwu et al., Biopharm Drug Dispos 1997: subkutan opløsning hos tre mænd; beta-fasens halveringstider 0,8 til 1,7 timer. Omkring én time er en afrundet reference fra dette lille forsøg og ikke implantatfrigivelse eller en generel værdi for Scenesse-præparatet ved EPP.",
			el: "Ugwu et al., Biopharm Drug Dispos 1997: υποδόριο διάλυμα σε τρεις άνδρες, χρόνοι ημιζωής β-φάσης 0,8 έως 1,7 ώρες. Η περίπου μία ώρα είναι στρογγυλοποιημένη αναφορά μικρής μελέτης διαλύματος, όχι χρόνος απελευθέρωσης εμφυτεύματος ή γενικό χαρακτηριστικό του φαρμάκου για EPP.",
			cs: "Ugwu et al., Biopharm Drug Dispos 1997: subkutánní roztok u tří mužů, poločasy beta-fáze 0,8 až 1,7 hodiny. Přibližně jedna hodina je zaokrouhlenou referencí malé studie roztoku, nikoli údajem uvolňování implantátu nebo farmakokinetikou léčiva pro erytropoetickou protoporfyrii.",
			fi: "Ugwu et al., Biopharm Drug Dispos 1997: ihonalainen liuos kolmella miehellä, beetavaiheen puoliintumisaika 0,8–1,7 tuntia. Noin tunti on pyöristetty pienen liuostutkimuksen viite, ei implantin vapautumisaika tai EPP-lääkevalmisteen farmakokinetiikan yleiskuvaus.",
			ro: "Ugwu et al., Biopharm Drug Dispos 1997: soluție subcutanată la trei bărbați, timp beta de înjumătățire 0,8-1,7 ore; circa o oră este o referință rotunjită a studiului mic, fără echivalare cu eliberarea din implantul EPP.",
			hu: "Ugwu et al., Biopharm Drug Dispos 1997: három férfi bőr alatti oldatvizsgálata; bétafázisú felezési idők 0,8–1,7 óra. A körülbelül egy óra kerekített, kis mintás oldatreferencia, nem a Scenesse implantátum leadása vagy EPP-gyógyszerének általános értéke.",
			sk: "Ugwu et al., Biopharm Drug Dispos 1997: podkožný roztok u troch mužov, polčas beta-fázy 0,8 až 1,7 hodiny. Približne jedna hodina je zaokrúhlená referencia malej štúdie roztoku; nie uvoľňovanie z implantátu ani všeobecná hodnota lieku pri EPP.",
			tr: "Ugwu et al., Biopharm Drug Dispos 1997: üç erkekte deri altı çözeltiyle beta fazı yarı ömür 0,8–1,7 saat. Yaklaşık bir saat bu küçük çözelti araştırmasının yuvarlatılmış referansıdır; Scenesse implantının salınımı veya EPP ilaç farmakokinetiğiyle eş tutulamaz.",
			nb: "Ugwu et al., Biopharm Drug Dispos 1997: subkutan løsning hos tre menn, beta-fase-halveringstid 0,8–1,7 timer. Én time er en avrundet referanse fra det lille forsøket, ikke implantatfrigjøring eller generell Scenesse-kinetikk.",
			bg: "Ugwu et al., Biopharm Drug Dispos 1997: подкожен разтвор при трима мъже; полуживоти в β-фазата 0,8-1,7 h. Около 1 h е закръглена референция от малко проучване на разтвор, не освобождаване от Scenesse имплант или обща EPP лекарствена стойност.",
			hr: "Ugwu et al., Biopharm Drug Dispos 1997: potkožna otopina u tri muškarca, poluvijek beta-faze 0,8 do 1,7 sati. Oko jednog sata zaokružena je referenca malog pokusa s otopinom; ne označava oslobađanje implantata Scenesse ili EPP-pripravka.",
			sl: "Ugwu et al., Biopharm Drug Dispos 1997: podkožna raztopina pri treh moških, razpolovni časi faze beta 0,8 do 1,7 ure. Približno ena ura je zaokrožena referenca majhne študije raztopine, ne označuje sproščanja iz vsadka Scenesse ali vrednosti zdravila za EPP.",
			sr: "Ugwu et al., Biopharm Drug Dispos 1997: potkožni rastvor kod tri muškarca, poluvremena beta faze 0,8 do 1,7 sati. Oko jedan sat je zaokružena referenca male studije rastvora, ne označava oslobađanje implantata Scenesse ili vrednost EPP leka.",
			bs: "Ugwu et al., Biopharm Drug Dispos 1997: potkožna otopina kod tri muškarca, poluvremena beta faze 0,8 do 1,7 sati. Oko jednog sata zaokružena je referenca male studije otopine, ne označava oslobađanje implantata Scenesse ili vrijednost EPP lijeka.",
			mk: "Ugwu et al., Biopharm Drug Dispos 1997: поткожен раствор кај три мажи, полувремиња на бета-фазата 0,8 до 1,7 часа. Околу еден час е заокружена референца од мала студија со раствор, не означува ослободување од имплантот Scenesse или вредност на EPP препарат.",
			lt: "Ugwu et al., Biopharm Drug Dispos 1997: po oda vartojamas tirpalas, tirtas su trimis vyrais, beta fazės pusėjimo trukmės 0,8 iki 1,7 valandos. Apie vieną valandą yra suapvalinta nuoroda iš to mažo tirpalo tyrimo, o ne implantų atpalaidavimo parametras ar patvirtinto EPP vaisto farmakokinetinė vertė.",
			lv: "Ugwu et al., Biopharm Drug Dispos 1997: subkutāns šķīdums, pētīts ar trim vīriešiem, beta fāzes pussabrukšanas laiki 0,8 līdz 1,7 stundas. Apmēram viena stunda ir noapaļota atsauce no šī nelielā šķīduma pētījuma, nevis implanta atbrīvošanās parametrs vai apstiprināto EPP medikamenta farmakokinētiskā vērtība.",
			et: "Ugwu et al., Biopharm Drug Dispos 1997: nahaalune lahus, mida uuriti kolmel mehel, beeta-faasi poolväärtusajad 0,8 kuni 1,7 tundi. Ligikaudu üks tund on ümardatud viide sellest väikesest lahusega uuringust, mitte implantaadi vabanemise parameeter ega heakskiidetud EPP ravimi farmakokineetiline väärtus.",
		},
		frequency: "—",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: isFiveProductLaunchLive("melanotan-1"),
		bergdorfProductSlug: isFiveProductLaunchLive("melanotan-1") ? "melanotan-1" : undefined,
		relatedPeptides: [],
		cas: "75921-69-6",
		molecularFormula: "C₇₈H₁₁₁N₂₁O₁₉",
		molarMass: "1 647 g/mol",
		synonyms: ["MT-I", "MT-1", "Afamelanotide", "[Nle4-D-Phe7]-α-MSH"],
		studyReferences: [
			{
				author: "Ugwu et al.",
				journal: "Biopharm Drug Dispos",
				year: 1997,
				title: "Skin pigmentation and pharmacokinetics of melanotan-I in humans",
				url: "https://pubmed.ncbi.nlm.nih.gov/9113347/",
			},
			{
				author: "Langendonk et al.",
				journal: "N Engl J Med",
				year: 2015,
				title: "Afamelanotide for erythropoietic protoporphyria",
				url: "https://pubmed.ncbi.nlm.nih.gov/26132941/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "sermorelin",
		name: {
			en: "Sermorelin",
			de: "Sermorelin",
			it: "Sermorelina",
			es: "Sermorelin",
			fr: "Sermoréline",
			nl: "Sermoreline",
			pt: "Sermorelina",
			"pt-br": "Sermorelina",
			pl: "Sermorelina",
			sv: "Sermorelin",
			da: "Sermorelin",
			el: "Sermorelin",
			cs: "Sermorelin",
			fi: "Sermoreliini",
			ro: "Sermorelin",
			hu: "Sermorelin",
			sk: "Sermorelín",
			tr: "Sermorelin",
			nb: "Sermorelin",
			bg: "Серморелин (Sermorelin)",
			hr: "Sermorelin",
			sl: "Sermorelin",
			sr: "Sermorelin",
			bs: "Sermorelin",
			mk: "Серморелин (Sermorelin)",
			lt: "Sermorelin",
			lv: "Sermorelin",
			et: "Sermorelin",
		},
		category: {
			en: "GHRH Analog",
			de: "GHRH-Analog",
			it: "Analogo del GHRH",
			es: "Análogo de GHRH",
			fr: "Analogue de la GHRH",
			nl: "GHRH-analoog",
			pt: "Análogo de GHRH",
			"pt-br": "Análogo de GHRH",
			pl: "Analog GHRH",
			sv: "GHRH-analog",
			da: "GHRH-analog",
			el: "Ανάλογο GHRH",
			cs: "Analog GHRH",
			fi: "GHRH-analogi",
			ro: "Analog GHRH",
			hu: "GHRH-analóg",
			sk: "Analóg GHRH",
			tr: "GHRH analoğu",
			nb: "GHRH-analog",
			bg: "Аналог на GHRH",
			hr: "Analog GHRH-a",
			sl: "GHRH analog",
			sr: "GHRH analog",
			bs: "GHRH analog",
			mk: "GHRH аналог",
			lt: "GHRH analogas",
			lv: "GHRH analogs",
			et: "GHRH analoog",
		},
		defaultVialSizes: [5],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.1,
		doseRange: { min: 0.05, max: 0.3 },
		doseUnit: "mcg",
		dosePresets: [0.05, 0.1, 0.2, 0.3],
		halfLife: "~10–20 min",
		frequency: "1–3x daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "sermorelin",
		relatedPeptides: ["cjc-1295-no-dac", "ipamorelin", "hexarelin", "ghrp-6", "ghrp-2"],
		synonyms: ["GHRH(1-29)-amide", "GRF(1-29)-NH2"],
	},
	{
		slug: "tesamorelin",
		name: {
			en: "Tesamorelin",
			de: "Tesamorelin",
			it: "Tesamorelin",
			es: "Tesamorelina",
			fr: "Tésamoréline",
			nl: "Tesamoreline",
			pt: "Tesamorelina",
			"pt-br": "Tesamorelina",
			pl: "Tesamorelina",
			sv: "Tesamorelin",
			da: "Tesamorelin",
			el: "Tesamorelin",
			cs: "Tesamorelin",
			fi: "Tesamoreliini",
			ro: "Tesamorelin",
			hu: "Tesamorelin",
			sk: "Tesamorelín",
			tr: "Tesamorelin",
			nb: "Tesamorelin",
			bg: "Tesamorelin",
			hr: "Tesamorelin",
			sl: "Tesamorelin",
			sr: "Tesamorelin",
			bs: "Tesamorelin",
			mk: "Тезаморелин (Tesamorelin)",
			lt: "Tesamorelin",
			lv: "Tesamorelin",
			et: "Tesamorelin",
		},
		category: {
			en: "GHRH Analog",
			de: "GHRH-Analog",
			it: "Analogo del GHRH",
			es: "Análogo de GHRH",
			fr: "Analogue de la GHRH",
			nl: "GHRH-analoog",
			pt: "Análogo de GHRH",
			"pt-br": "Análogo de GHRH",
			pl: "Analog GHRH",
			sv: "GHRH-analog",
			da: "GHRH-analog",
			el: "Ανάλογο GHRH",
			cs: "Analog GHRH",
			fi: "GHRH-analogi",
			ro: "Analog GHRH",
			hu: "GHRH-analóg",
			sk: "Analóg GHRH",
			tr: "GHRH analoğu",
			nb: "GHRH-analog",
			bg: "GHRH аналог",
			hr: "GHRH analog",
			sl: "GHRH analog",
			sr: "GHRH analog",
			bs: "GHRH analog",
			mk: "GHRH аналог",
			lt: "GHRH analogas",
			lv: "GHRH analogs",
			et: "GHRH analoog",
		},
		defaultVialSizes: [10, 20],
		defaultVialSize: 20,
		defaultBacWater: 2,
		defaultDose: 1,
		doseRange: { min: 1, max: 2 },
		doseUnit: "mg",
		dosePresets: [1, 1.5, 2],
		halfLife: "",
		frequency: "daily",
		recommendedSyringe: 0.5,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "tesamorelin",
		relatedPeptides: ["sermorelin", "cjc-1295-no-dac", "ipamorelin"],
		cas: "218949-48-5",
		molecularFormula: "C₂₂₁H₃₆₆N₇₂O₆₇S",
		molarMass: "5 136 g/mol",
		synonyms: ["TH9507", "Egrifta", "Egrifta SV", "GRF 1-44 analog"],
		studyReferences: [
			{
				author: "Falutz et al.",
				journal: "NEJM",
				year: 2007,
				title: "Metabolic effects of growth hormone-releasing factor in HIV",
				url: "https://pubmed.ncbi.nlm.nih.gov/18057338/",
			},
			{
				author: "Falutz et al.",
				journal: "JAIDS",
				year: 2010,
				title: "Effects of tesamorelin in HIV abdominal fat accumulation",
				url: "https://pubmed.ncbi.nlm.nih.gov/20101189/",
			},
			{
				author: "Stanley et al.",
				journal: "JCEM",
				year: 2019,
				title: "Tesamorelin decreases muscle fat in adults with HIV",
				url: "https://pubmed.ncbi.nlm.nih.gov/31237318/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "thymosin-alpha-1",
		name: {
			en: "Thymosin Alpha-1",
			de: "Thymosin Alpha-1",
			it: "Timosina alfa-1",
			es: "Timosina alfa-1",
			fr: "Thymosine alpha-1",
			nl: "Thymosine alfa-1",
			pt: "Timosina alfa-1",
			"pt-br": "Timosina alfa-1",
			pl: "Tymozyna alfa-1",
			sv: "Tymosin alfa-1",
			da: "Thymosin alfa-1",
			el: "Thymosin Alpha-1",
			cs: "Thymosin alfa-1",
			fi: "Tymosiini alfa-1",
			ro: "Timosină alfa-1",
			hu: "Timozin-α1",
			sk: "Tymozín alfa-1",
			tr: "Timozin alfa-1",
			nb: "Thymosin alfa-1",
			bg: "Thymosin Alpha-1",
			hr: "Timosin alfa-1",
			sl: "Thymosin Alpha-1",
			sr: "Thymosin Alpha-1",
			bs: "Thymosin Alpha-1",
			mk: "Тимозин алфа-1 (Thymosin Alpha-1)",
			lt: "Thymosin Alpha-1",
			lv: "Thymosin Alpha-1",
			et: "Thymosin Alpha-1",
		},
		category: {
			en: "Thymic Peptide",
			de: "Thymisches Peptid",
			it: "Peptide timico",
			es: "Péptido tímico",
			fr: "Peptide thymique",
			nl: "Thymuspeptide",
			pt: "Péptido tímico",
			"pt-br": "Peptídeo tímico",
			pl: "Peptyd grasiczy",
			sv: "Tymisk peptid",
			da: "Thymisk peptid",
			el: "Θυμικό πεπτίδιο",
			cs: "Thymový peptid",
			fi: "Kateenkorvapeptidi",
			ro: "Peptidă timică",
			hu: "Csecsemőmirigy-peptid",
			sk: "Týmusový peptid",
			tr: "Timus peptidi",
			nb: "Thymuspeptid",
			bg: "Тимусен пептид",
			hr: "Timički peptid",
			sl: "Timusni peptid",
			sr: "Timički peptid",
			bs: "Timički peptid",
			mk: "Тимусен пептид",
			lt: "Timuso peptidas",
			lv: "Timusa peptīds",
			et: "Tüümusepetiid",
		},
		defaultVialSizes: [1.6, 5, 10],
		defaultVialSize: 10,
		defaultBacWater: 1,
		defaultDose: 1.6,
		doseRange: { min: 0.8, max: 3.2 },
		doseUnit: "mg",
		dosePresets: [0.8, 1.6, 3.2],
		halfLife: "~hours",
		frequency: "2–7x weekly",
		recommendedSyringe: 0.5,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "thymosin-alpha-1",
		relatedPeptides: ["tb-500", "bpc-157", "kpv"],
	},
	{
		slug: "epithalon",
		name: {
			en: "Epithalon",
			de: "Epithalon",
			it: "Epithalon",
			es: "Epithalon",
			fr: "Épithalon",
			nl: "Epithalon",
			pt: "Epithalon",
			"pt-br": "Epithalon",
			pl: "Epithalon",
			sv: "Epithalon",
			da: "Epithalon",
			el: "Epithalon",
			cs: "Epithalon",
			fi: "Epithalon",
			ro: "Epithalon",
			hu: "Epithalon",
			sk: "Epithalon",
			tr: "Epithalon",
			nb: "Epithalon",
			bg: "Epithalon",
			hr: "Epithalon",
			sl: "Epithalon",
			sr: "Epithalon",
			bs: "Epithalon",
			mk: "Епиталон (Epithalon)",
			lt: "Epithalon",
			lv: "Epithalon",
			et: "Epithalon",
		},
		category: {
			en: "Longevity",
			de: "Langlebigkeit",
			it: "Longevità",
			es: "Longevidad",
			fr: "Longévité",
			nl: "Levensduur",
			pt: "Longevidade",
			"pt-br": "Longevidade",
			pl: "Długowieczność",
			sv: "Livslängd",
			da: "Aldringsforskning",
			el: "Μακροζωία",
			cs: "Dlouhověkost",
			fi: "Pitkäikäisyys",
			ro: "Longevitate",
			hu: "Hosszú élettartam",
			sk: "Dlhovekosť",
			tr: "Uzun yaşam",
			nb: "Levetid",
			bg: "Дълголетие",
			hr: "Dugovječnost",
			sl: "Dolgoživost",
			sr: "Dugovečnost",
			bs: "Dugovječnost",
			mk: "Долговечност",
			lt: "Ilgaamžiškumas",
			lv: "Ilgmūžība",
			et: "Pikaajalisus",
		},
		defaultVialSizes: [10, 20, 50],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 5,
		doseRange: { min: 2, max: 10 },
		doseUnit: "mg",
		dosePresets: [2, 3, 5, 7, 10],
		halfLife: "~hours",
		frequency: "daily (cycles)",
		recommendedSyringe: 0.5,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "epithalon",
		relatedPeptides: ["ss-31", "mots-c", "selank", "dsip"],
		cas: "307297-39-8",
		molecularFormula: "C₁₄H₂₂N₄O₉",
		molarMass: "390 g/mol",
		synonyms: ["Epitalon", "AEDG-Peptid", "Ala-Glu-Asp-Gly"],
		studyReferences: [
			{
				author: "Khavinson et al.",
				journal: "Bull Exp Biol Med",
				year: 2003,
				title: "Epithalon peptide induces telomerase activity and telomere elongation in human somatic cells",
				url: "https://pubmed.ncbi.nlm.nih.gov/12937682/",
			},
			{
				author: "Kossoy et al.",
				journal: "In Vivo",
				year: 2006,
				title:
					"Effect of the synthetic pineal peptide epitalon on spontaneous carcinogenesis in female C3H/He mice",
				url: "https://pubmed.ncbi.nlm.nih.gov/16634527/",
			},
			{
				author: "Vinogradova et al.",
				journal: "Bull Exp Biol Med",
				year: 2007,
				title:
					"Effect of Ala-Glu-Asp-Gly peptide on life span and development of spontaneous tumors in female rats exposed to different illumination regimens",
				url: "https://pubmed.ncbi.nlm.nih.gov/18856211/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "kpv",
		name: {
			en: "KPV",
			de: "KPV",
			it: "KPV",
			es: "KPV",
			fr: "KPV",
			nl: "KPV",
			pt: "KPV",
			"pt-br": "KPV",
			pl: "KPV",
			sv: "KPV",
			da: "KPV",
			el: "KPV",
			cs: "KPV",
			fi: "KPV",
			ro: "KPV",
			hu: "KPV",
			sk: "KPV",
			tr: "KPV",
			nb: "KPV",
			bg: "KPV",
			hr: "KPV",
			sl: "KPV",
			sr: "KPV",
			bs: "KPV",
			mk: "KPV",
			lt: "KPV",
			lv: "KPV",
			et: "KPV",
		},
		category: {
			en: "Immune / Barrier",
			de: "Immunsystem / Barriere",
			it: "Immunità / Barriera",
			es: "Inmunidad / barrera",
			fr: "Immunité / Barrière",
			nl: "Immuniteit / barrièrefunctie",
			pt: "Imunidade / Barreira",
			"pt-br": "Imunidade / Barreira",
			pl: "Odporność / Funkcje barierowe",
			sv: "Immunförsvar / barriär",
			da: "Immunforsvar / barriere",
			el: "Ανοσία / Φραγμός",
			cs: "Imunita / bariéra",
			fi: "Immuniteetti / kudoseste",
			ro: "Imunitate / Barieră",
			hu: "Immunrendszer / barrier",
			sk: "Imunita / bariéra",
			tr: "Bağışıklık / bariyer",
			nb: "Immunfunksjon / barriere",
			bg: "Имунна / Бариера",
			hr: "Imunološka funkcija / barijera",
			sl: "Imunski sistem / pregrada",
			sr: "Imunski sistem / barijera",
			bs: "Imunski sistem / barijera",
			mk: "Имун систем / бариера",
			lt: "Imuninė sistema / barjeras",
			lv: "Imūnā sistēma / barjera",
			et: "Immuunsüsteem / barjäär",
		},
		defaultVialSizes: [5, 10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.25,
		doseRange: { min: 0.1, max: 1 },
		doseUnit: "mcg",
		dosePresets: [0.2, 0.25, 0.5, 0.75, 1],
		halfLife: "~short",
		frequency: "daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "kpv",
		relatedPeptides: ["bpc-157", "selank", "thymosin-alpha-1"],
		cas: "67727-97-3",
		molecularFormula: "C₁₆H₃₀N₄O₄",
		molarMass: "342 g/mol",
		synonyms: ["Lys-Pro-Val", "α-MSH(11-13)"],
		studyReferences: [
			{
				author: "Dalmasso et al.",
				journal: "Gastroenterology",
				year: 2008,
				title: "PepT1-mediated tripeptide KPV uptake reduces intestinal inflammation",
				url: "https://pubmed.ncbi.nlm.nih.gov/18061177/",
			},
			{
				author: "Kannengiesser et al.",
				journal: "Inflamm Bowel Dis",
				year: 2008,
				title:
					"Melanocortin-derived tripeptide KPV has anti-inflammatory potential in murine models of inflammatory bowel disease",
				url: "https://pubmed.ncbi.nlm.nih.gov/18092346/",
			},
			{
				author: "Xiao et al.",
				journal: "Mol Ther",
				year: 2017,
				title:
					"Orally targeted delivery of tripeptide KPV via hyaluronic acid-functionalized nanoparticles efficiently alleviates ulcerative colitis",
				url: "https://pubmed.ncbi.nlm.nih.gov/28143741/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "hgh-fragment-176-191",
		name: {
			en: "HGH Fragment 176-191",
			de: "HGH-Fragment 176-191",
			it: "Frammento HGH 176-191",
			es: "Fragmento de HGH 176-191",
			fr: "Fragment HGH 176-191",
			nl: "HGH-fragment 176-191",
			pt: "Fragmento de HGH 176-191",
			"pt-br": "Fragmento HGH 176-191",
			pl: "Fragment HGH 176-191",
			sv: "HGH-fragment 176-191",
			da: "HGH-fragment 176-191",
			el: "Τμήμα HGH 176-191",
			cs: "Fragment HGH 176-191",
			fi: "HGH-fragmentti 176–191",
			ro: "Fragment HGH 176-191",
			hu: "HGH 176–191 fragmentum",
			sk: "HGH fragment 176-191",
			tr: "HGH fragmanı 176-191",
			nb: "HGH-fragment 176-191",
			bg: "HGH Fragment 176-191",
			hr: "HGH fragment 176-191",
			sl: "HGH Fragment 176-191",
			sr: "HGH Fragment 176-191",
			bs: "HGH Fragment 176-191",
			mk: "HGH фрагмент 176-191",
			lt: "HGH Fragment 176-191",
			lv: "HGH Fragment 176-191",
			et: "HGH Fragment 176-191",
		},
		category: {
			en: "Metabolic Fragment",
			de: "Metabolisches Fragment",
			it: "Frammento metabolico",
			es: "Fragmento metabólico",
			fr: "Fragment métabolique",
			nl: "Metabool fragment",
			pt: "Fragmento metabólico",
			"pt-br": "Fragmento metabólico",
			pl: "Fragment metaboliczny",
			sv: "Metaboliskt fragment",
			da: "Metabolisk fragment",
			el: "Μεταβολικό τμήμα",
			cs: "Metabolický fragment",
			fi: "Aineenvaihduntafragmentti",
			ro: "Fragment metabolic",
			hu: "Metabolikus fragmentum",
			sk: "Metabolický fragment",
			tr: "Metabolik fragman",
			nb: "Metabolsk fragment",
			bg: "Метаболитен фрагмент",
			hr: "Metabolički fragment",
			sl: "Presnovni fragment",
			sr: "Metabolički fragment",
			bs: "Metabolički fragment",
			mk: "Метаболичен фрагмент",
			lt: "Metabolinis fragmentas",
			lv: "Metabolais fragments",
			et: "Metaboolne fragment",
		},
		defaultVialSizes: [2, 5, 10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.25,
		doseRange: { min: 0.1, max: 0.5 },
		doseUnit: "mcg",
		dosePresets: [0.1, 0.25, 0.4, 0.5],
		halfLife: "",
		frequency: "1–2x daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: false,
		relatedPeptides: ["aod-9604", "ipamorelin", "tesamorelin"],
	},
	{
		slug: "semax",
		name: {
			en: "Semax",
			de: "Semax",
			it: "Semax",
			es: "Semax",
			fr: "Semax",
			nl: "Semax",
			pt: "Semax",
			"pt-br": "Semax",
			pl: "Semax",
			sv: "Semax",
			da: "Semax",
			el: "Semax",
			cs: "Semax",
			fi: "Semax",
			ro: "Semax",
			hu: "Semax",
			sk: "Semax",
			tr: "Semax",
			nb: "Semax",
			bg: "Semax",
			hr: "Semax",
			sl: "Semax",
			sr: "Semax",
			bs: "Semax",
			mk: "Семакс (Semax)",
			lt: "Semax",
			lv: "Semax",
			et: "Semax",
		},
		category: {
			en: "Cognitive",
			de: "Kognitiv",
			it: "Cognitivo",
			es: "Cognitivo",
			fr: "Cognition",
			nl: "Cognitief",
			pt: "Cognitivo",
			"pt-br": "Cognição",
			pl: "Funkcje poznawcze",
			sv: "Kognitiv",
			da: "Kognition",
			el: "Γνωστική λειτουργία",
			cs: "Kognice",
			fi: "Kognitio",
			ro: "Cognitiv",
			hu: "Kognitív",
			sk: "Kognícia",
			tr: "Bilişsel",
			nb: "Kognitiv",
			bg: "Когнитивен",
			hr: "Kognitivno",
			sl: "Kognitivno",
			sr: "Kognitivno",
			bs: "Kognitivno",
			mk: "Когнитивно",
			lt: "Kognityvinis",
			lv: "Kognitīvs",
			et: "Kognitiivne",
		},
		defaultVialSizes: [10, 30],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.3,
		doseRange: { min: 0.1, max: 1 },
		doseUnit: "mcg",
		dosePresets: [0.1, 0.3, 0.6, 1],
		halfLife: "~minutes",
		frequency: "daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "semax",
		relatedPeptides: ["n-acetyl-semax", "selank", "dsip"],
		cas: "80714-61-0",
		molecularFormula: "C₃₇H₅₁N₉O₁₀S",
		molarMass: "814 g/mol",
		synonyms: ["MEHFPGP", "ACTH(4-7)PGP", "Met-Glu-His-Phe-Pro-Gly-Pro", "ACTH(4-10) analog"],
		studyReferences: [
			{
				author: "Dolotov et al.",
				journal: "Brain Research",
				year: 2006,
				title: "Semax regulates BDNF and TrkB expression in the rat hippocampus",
				url: "https://pubmed.ncbi.nlm.nih.gov/16996037/",
			},
			{
				author: "Romanova et al.",
				journal: "Bull Exp Biol Med",
				year: 2006,
				title: "Neuroprotective and antiamnesic effects of Semax in cerebral cortex ischemic infarction",
				url: "https://pubmed.ncbi.nlm.nih.gov/17603664/",
			},
			{
				author: "Skvortsova et al.",
				journal: "Zh Nevrol Psikhiatr Korsakova",
				year: 1999,
				title: "Mechanisms of neuroprotective effect of Semax in acute ischemic stroke",
				url: "https://pubmed.ncbi.nlm.nih.gov/10358912/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "n-acetyl-semax",
		name: {
			en: "N-Acetyl Semax",
			de: "N-Acetyl-Semax",
			it: "N-Acetil Semax",
			es: "N-acetil-Semax",
			fr: "N-acétyl Semax",
			nl: "N-acetyl-Semax",
			pt: "N-acetil Semax",
			"pt-br": "N-acetil Semax",
			pl: "N-acetylo-Semax",
			sv: "N-acetyl-Semax",
			da: "N-acetyl-Semax",
			el: "N-Acetyl Semax",
			cs: "N-acetyl-Semax",
			fi: "N-asetyyli-Semax",
			ro: "N-acetil Semax",
			hu: "N-acetil-Semax",
			sk: "N-acetyl Semax",
			tr: "N-Asetil Semax",
			nb: "N-acetyl-Semax",
			bg: "N-Acetyl Semax",
			hr: "N-acetil Semax",
			sl: "N-Acetyl Semax",
			sr: "N-Acetyl Semax",
			bs: "N-Acetyl Semax",
			mk: "N-ацетил Семакс (N-Acetyl Semax)",
			lt: "N-Acetyl Semax",
			lv: "N-Acetyl Semax",
			et: "N-Acetyl Semax",
		},
		category: {
			en: "Cognitive",
			de: "Kognitiv",
			it: "Cognitivo",
			es: "Cognitivo",
			fr: "Cognition",
			nl: "Cognitief",
			pt: "Cognitivo",
			"pt-br": "Cognição",
			pl: "Funkcje poznawcze",
			sv: "Kognitiv",
			da: "Kognition",
			el: "Γνωστική λειτουργία",
			cs: "Kognice",
			fi: "Kognitio",
			ro: "Cognitiv",
			hu: "Kognitív",
			sk: "Kognícia",
			tr: "Bilişsel",
			nb: "Kognitiv",
			bg: "Когнитивен",
			hr: "Kognitivno",
			sl: "Kognitivno",
			sr: "Kognitivno",
			bs: "Kognitivno",
			mk: "Когнитивно",
			lt: "Kognityvinis",
			lv: "Kognitīvs",
			et: "Kognitiivne",
		},
		defaultVialSizes: [5, 10],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.3,
		doseRange: { min: 0.1, max: 1 },
		doseUnit: "mcg",
		dosePresets: [0.1, 0.3, 0.6, 1],
		halfLife: "",
		frequency: "daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: false,
		relatedPeptides: ["semax", "selank", "dsip"],
	},
	{
		slug: "hexarelin",
		name: {
			en: "Hexarelin",
			de: "Hexarelin",
			it: "Hexarelin",
			es: "Hexarelin",
			fr: "Hexaréline",
			nl: "Hexareline",
			pt: "Hexarelina",
			"pt-br": "Hexarelina",
			pl: "Heksarelina",
			sv: "Hexarelin",
			da: "Hexarelin",
			el: "Hexarelin",
			cs: "Hexarelin",
			fi: "Heksareliini",
			ro: "Hexarelin",
			hu: "Hexarelin",
			sk: "Hexarelín",
			tr: "Heksarelin",
			nb: "Hexarelin",
			bg: "Hexarelin",
			hr: "Heksarelin",
			sl: "Hexarelin",
			sr: "Hexarelin",
			bs: "Hexarelin",
			mk: "Хексарелин (Hexarelin)",
			lt: "Hexarelin",
			lv: "Hexarelin",
			et: "Hexarelin",
		},
		category: {
			en: "GHRP",
			de: "GHRP",
			it: "GHRP",
			es: "GHRP",
			fr: "GHRP",
			nl: "GHRP",
			pt: "GHRP",
			"pt-br": "GHRP",
			pl: "GHRP",
			sv: "GHRP",
			da: "GHRP",
			el: "GHRP",
			cs: "GHRP",
			fi: "GHRP",
			ro: "GHRP",
			hu: "GHRP",
			sk: "GHRP",
			tr: "GHRP",
			nb: "GHRP",
			bg: "GHRP",
			hr: "GHRP",
			sl: "GHRP",
			sr: "GHRP",
			bs: "GHRP",
			mk: "GHRP",
			lt: "GHRP",
			lv: "GHRP",
			et: "GHRP",
		},
		defaultVialSizes: [2, 5],
		defaultVialSize: 2,
		defaultBacWater: 2,
		defaultDose: 0.1,
		doseRange: { min: 0.05, max: 0.2 },
		doseUnit: "mcg",
		dosePresets: [0.05, 0.1, 0.15, 0.2],
		halfLife: "",
		frequency: "2–3x daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: false,
		relatedPeptides: ["ipamorelin", "sermorelin", "cjc-1295-no-dac", "ghrp-6", "ghrp-2"],
	},
	{
		slug: "ghrp-6",
		name: {
			en: "GHRP-6",
			de: "GHRP-6",
			it: "GHRP-6",
			es: "GHRP-6",
			fr: "GHRP-6",
			nl: "GHRP-6",
			pt: "GHRP-6",
			"pt-br": "GHRP-6",
			pl: "GHRP-6",
			sv: "GHRP-6",
			da: "GHRP-6",
			el: "GHRP-6",
			cs: "GHRP-6",
			fi: "GHRP-6",
			ro: "GHRP-6",
			hu: "GHRP-6",
			sk: "GHRP-6",
			tr: "GHRP-6",
			nb: "GHRP-6",
			bg: "GHRP-6",
			hr: "GHRP-6",
			sl: "GHRP-6",
			sr: "GHRP-6",
			bs: "GHRP-6",
			mk: "GHRP-6",
			lt: "GHRP-6",
			lv: "GHRP-6",
			et: "GHRP-6",
		},
		category: {
			en: "GHRP",
			de: "GHRP",
			it: "GHRP",
			es: "GHRP",
			fr: "GHRP",
			nl: "GHRP",
			pt: "GHRP",
			"pt-br": "GHRP",
			pl: "GHRP",
			sv: "GHRP",
			da: "GHRP",
			el: "GHRP",
			cs: "GHRP",
			fi: "GHRP",
			ro: "GHRP",
			hu: "GHRP",
			sk: "GHRP",
			tr: "GHRP",
			nb: "GHRP",
			bg: "GHRP",
			hr: "GHRP",
			sl: "GHRP",
			sr: "GHRP",
			bs: "GHRP",
			mk: "GHRP",
			lt: "GHRP",
			lv: "GHRP",
			et: "GHRP",
		},
		defaultVialSizes: [2, 5],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.1,
		doseRange: { min: 0.05, max: 0.3 },
		doseUnit: "mcg",
		dosePresets: [0.05, 0.1, 0.15, 0.2, 0.3],
		halfLife: "",
		frequency: "2–3x daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: false,
		relatedPeptides: ["ghrp-2", "ipamorelin", "hexarelin", "cjc-1295-no-dac"],
	},
	{
		slug: "ghrp-2",
		name: {
			en: "GHRP-2",
			de: "GHRP-2",
			it: "GHRP-2",
			es: "GHRP-2",
			fr: "GHRP-2",
			nl: "GHRP-2",
			pt: "GHRP-2",
			"pt-br": "GHRP-2",
			pl: "GHRP-2",
			sv: "GHRP-2",
			da: "GHRP-2",
			el: "GHRP-2",
			cs: "GHRP-2",
			fi: "GHRP-2",
			ro: "GHRP-2",
			hu: "GHRP-2",
			sk: "GHRP-2",
			tr: "GHRP-2",
			nb: "GHRP-2",
			bg: "GHRP-2",
			hr: "GHRP-2",
			sl: "GHRP-2",
			sr: "GHRP-2",
			bs: "GHRP-2",
			mk: "GHRP-2",
			lt: "GHRP-2",
			lv: "GHRP-2",
			et: "GHRP-2",
		},
		category: {
			en: "GHRP",
			de: "GHRP",
			it: "GHRP",
			es: "GHRP",
			fr: "GHRP",
			nl: "GHRP",
			pt: "GHRP",
			"pt-br": "GHRP",
			pl: "GHRP",
			sv: "GHRP",
			da: "GHRP",
			el: "GHRP",
			cs: "GHRP",
			fi: "GHRP",
			ro: "GHRP",
			hu: "GHRP",
			sk: "GHRP",
			tr: "GHRP",
			nb: "GHRP",
			bg: "GHRP",
			hr: "GHRP",
			sl: "GHRP",
			sr: "GHRP",
			bs: "GHRP",
			mk: "GHRP",
			lt: "GHRP",
			lv: "GHRP",
			et: "GHRP",
		},
		defaultVialSizes: [2, 5],
		defaultVialSize: 5,
		defaultBacWater: 2,
		defaultDose: 0.1,
		doseRange: { min: 0.05, max: 0.25 },
		doseUnit: "mcg",
		dosePresets: [0.05, 0.1, 0.15, 0.25],
		halfLife: "",
		frequency: "2–3x daily",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: false,
		relatedPeptides: ["ghrp-6", "ipamorelin", "hexarelin", "cjc-1295-no-dac"],
	},
	{
		slug: "dsip",
		name: {
			en: "DSIP",
			de: "DSIP",
			it: "DSIP",
			es: "DSIP",
			fr: "DSIP",
			nl: "DSIP",
			pt: "DSIP",
			"pt-br": "DSIP",
			pl: "DSIP",
			sv: "DSIP",
			da: "DSIP",
			el: "DSIP",
			cs: "DSIP",
			fi: "DSIP",
			ro: "DSIP",
			hu: "DSIP",
			sk: "DSIP",
			tr: "DSIP",
			nb: "DSIP",
			bg: "DSIP",
			hr: "DSIP",
			sl: "DSIP",
			sr: "DSIP",
			bs: "DSIP",
			mk: "DSIP",
			lt: "DSIP",
			lv: "DSIP",
			et: "DSIP",
		},
		category: {
			en: "Sleep",
			de: "Schlaf",
			it: "Sonno",
			es: "Sueño",
			fr: "Sommeil",
			nl: "Slaap",
			pt: "Sono",
			"pt-br": "Sono",
			pl: "Sen",
			sv: "Sömn",
			da: "Søvn",
			el: "Ύπνος",
			cs: "Spánek",
			fi: "Uni",
			ro: "Somn",
			hu: "Alvás",
			sk: "Spánok",
			tr: "Uyku",
			nb: "Søvn",
			bg: "Сън",
			hr: "San",
			sl: "Spanje",
			sr: "San",
			bs: "San",
			mk: "Сон",
			lt: "Miegas",
			lv: "Miegs",
			et: "Uni",
		},
		defaultVialSizes: [10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.2,
		doseRange: { min: 0.05, max: 0.5 },
		doseUnit: "mcg",
		dosePresets: [0.1, 0.2, 0.3, 0.5],
		halfLife: "~variable",
		frequency: "context-dependent",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "dsip",
		relatedPeptides: ["selank", "semax", "epithalon"],
		synonyms: ["Delta Sleep-Inducing Peptide", "Emideltide"],
	},
	{
		slug: "5-amino-1mq",
		name: {
			en: "5-Amino-1MQ",
			de: "5-Amino-1MQ",
			it: "5-Amino-1MQ",
			es: "5-Amino-1MQ",
			fr: "5-Amino-1MQ",
			nl: "5-Amino-1MQ",
			pt: "5-Amino-1MQ",
			"pt-br": "5-Amino-1MQ",
			pl: "5-Amino-1MQ",
			sv: "5-Amino-1MQ",
			da: "5-Amino-1MQ",
			el: "5-Amino-1MQ",
			cs: "5-Amino-1MQ",
			fi: "5-Amino-1MQ",
			ro: "5-Amino-1MQ",
			hu: "5-Amino-1MQ",
			sk: "5-Amino-1MQ",
			tr: "5-Amino-1MQ",
			nb: "5-Amino-1MQ",
			bg: "5-Amino-1MQ",
			hr: "5-Amino-1MQ",
			sl: "5-Amino-1MQ",
			sr: "5-Amino-1MQ",
			bs: "5-Amino-1MQ",
			mk: "5-Amino-1MQ",
			lt: "5-Amino-1MQ",
			lv: "5-Amino-1MQ",
			et: "5-Amino-1MQ",
		},
		category: {
			en: "Metabolic",
			de: "Metabolisch",
			it: "Metabolico",
			es: "Metabólico",
			fr: "Métabolique",
			nl: "Metabool",
			pt: "Metabólico",
			"pt-br": "Metabólico",
			pl: "Metabolizm",
			sv: "Metabolisk",
			da: "Metabolisk",
			el: "Μεταβολικό",
			cs: "Metabolické látky",
			fi: "Aineenvaihdunta",
			ro: "Metabolic",
			hu: "Metabolikus",
			sk: "Metabolické",
			tr: "Metabolik",
			nb: "Metabolsk",
			bg: "Метаболитен",
			hr: "Metabolički",
			sl: "Presnovno",
			sr: "Metabolički",
			bs: "Metabolički",
			mk: "Метаболичен",
			lt: "Metabolinis",
			lv: "Metabolais",
			et: "Metaboolne",
		},
		defaultVialSizes: [10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 5,
		doseRange: { min: 2.5, max: 10 },
		doseUnit: "mg",
		dosePresets: [2.5, 5, 7.5, 10],
		halfLife: "~hours",
		frequency: "daily or EOD",
		recommendedSyringe: 0.5,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "5-amino-1mq",
		relatedPeptides: ["mots-c", "ss-31", "aod-9604"],
		synonyms: ["5A1MQ", "5-amino-1-methylquinolinium"],
	},
	{
		slug: "pinealon",
		name: {
			en: "Pinealon",
			de: "Pinealon",
			it: "Pinealon",
			es: "Pinealon",
			fr: "Pinealon",
			nl: "Pinealon",
			pt: "Pinealon",
			"pt-br": "Pinealon",
			pl: "Pinealon",
			sv: "Pinealon",
			da: "Pinealon",
			el: "Pinealon",
			cs: "Pinealon",
			fi: "Pinealon",
			ro: "Pinealon",
			hu: "Pinealon",
			sk: "Pinealon",
			tr: "Pinealon",
			nb: "Pinealon",
			bg: "Пинеалон (Pinealon)",
			hr: "Pinealon",
			sl: "Pinealon",
			sr: "Pinealon",
			bs: "Pinealon",
			mk: "Пинеалон (Pinealon)",
			lt: "Pinealon",
			lv: "Pinealon",
			et: "Pinealon",
		},
		category: {
			en: "Research peptide",
			de: "Forschungspeptid",
			it: "Peptide di ricerca",
			es: "Péptido de investigación",
			fr: "Peptide de recherche",
			nl: "Onderzoekspeptide",
			pt: "Péptido de investigação",
			"pt-br": "Peptídeo de pesquisa",
			pl: "Peptyd badawczy",
			sv: "Forskningspeptid",
			da: "Forskningspeptid",
			el: "Ερευνητικό πεπτίδιο",
			cs: "Výzkumný peptid",
			fi: "Tutkimuspeptidi",
			ro: "Peptidă de cercetare",
			hu: "Kutatási peptid",
			sk: "Výskumný peptid",
			tr: "Araştırma peptidi",
			nb: "Forskningspeptid",
			bg: "Изследователски пептид",
			hr: "Istraživački peptid",
			sl: "Raziskovalni peptid",
			sr: "Istraživački peptid",
			bs: "Istraživački peptid",
			mk: "Истражувачки пептид",
			lt: "Tiriamasis peptidas",
			lv: "Pētniecības peptīds",
			et: "Uurimispeptiid",
		},
		calculatorMode: "research-dilution",
		defaultVialSizes: [10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.1,
		halfLife: "—",
		frequency: "—",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "pinealon",
		relatedPeptides: ["semax", "selank", "epithalon"],
		synonyms: ["EDR", "Glu-Asp-Arg"],
	},
	{
		slug: "survodutide",
		name: {
			en: "Survodutide",
			de: "Survodutid",
			it: "Survodutide",
			es: "Survodutide",
			fr: "Survodutide",
			nl: "Survodutide",
			pt: "Survodutida",
			"pt-br": "Survodutida",
			pl: "Survodutide",
			sv: "Survodutid",
			da: "Survodutid",
			el: "Survodutide",
			cs: "Survodutid",
			fi: "Survodutidi",
			ro: "Survodutide",
			hu: "Survodutide",
			sk: "Survodutid",
			tr: "Survodutide",
			nb: "Survodutid",
			bg: "Сурводутид (Survodutide)",
			hr: "Survodutid",
			sl: "Survodutide",
			sr: "Survodutide",
			bs: "Survodutide",
			mk: "Сурводутид (Survodutide)",
			lt: "Survodutide",
			lv: "Survodutide",
			et: "Survodutide",
		},
		category: {
			en: "GLP-1/Glucagon",
			de: "GLP-1/Glukagon",
			it: "GLP-1/glucagone",
			es: "GLP-1/glucagón",
			fr: "GLP-1/Glucagon",
			nl: "GLP-1/Glucagon",
			pt: "GLP-1/Glucagom",
			"pt-br": "GLP-1/Glucagon",
			pl: "GLP-1/Glukagon",
			sv: "GLP-1/glukagon",
			da: "GLP-1/glukagon",
			el: "GLP-1/Γλυκαγόνη",
			cs: "GLP-1/glukagon",
			fi: "GLP-1/glukagoni",
			ro: "GLP-1/Glucagon",
			hu: "GLP-1/glukagon",
			sk: "GLP-1/glukagón",
			tr: "GLP-1/glukagon",
			nb: "GLP-1/Glukagon",
			bg: "GLP-1/глюкагон",
			hr: "GLP-1/glukagon",
			sl: "GLP-1/glukagon",
			sr: "GLP-1/glukagon",
			bs: "GLP-1/glukagon",
			mk: "GLP-1/глукагон",
			lt: "GLP-1/glukagonas",
			lv: "GLP-1/glikagons",
			et: "GLP-1/glükagoon",
		},
		calculatorMode: "research-dilution",
		defaultVialSizes: [10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.1,
		halfLife: "—",
		frequency: "—",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "survodutide",
		relatedPeptides: ["retatrutide", "tirzepatid", "cagrilintide"],
		synonyms: ["BI 456906", "Survodutid"],
	},
	{
		slug: "kisspeptin-10",
		name: {
			en: "Kisspeptin-10",
			de: "Kisspeptin-10",
			it: "Kisspeptina-10",
			es: "Kisspeptin-10",
			fr: "Kisspeptine-10",
			nl: "Kisspeptine-10",
			pt: "Kisspeptina-10",
			"pt-br": "Kisspeptina-10",
			pl: "Kisspeptyna-10",
			sv: "Kisspeptin-10",
			da: "Kisspeptin-10",
			el: "Kisspeptin-10",
			cs: "Kisspeptin-10",
			fi: "Kisspeptiini-10",
			ro: "Kisspeptin-10",
			hu: "Kisspeptin-10",
			sk: "Kisspeptín-10",
			tr: "Kisspeptin-10",
			nb: "Kisspeptin-10",
			bg: "Киспептин-10 (Kisspeptin-10)",
			hr: "Kisspeptin-10",
			sl: "Kisspeptin-10",
			sr: "Kisspeptin-10",
			bs: "Kisspeptin-10",
			mk: "Киспептин-10 (Kisspeptin-10)",
			lt: "Kisspeptin-10",
			lv: "Kisspeptin-10",
			et: "Kisspeptin-10",
		},
		category: {
			en: "KISS1R / GPR54",
			de: "KISS1R / GPR54",
			it: "KISS1R / GPR54",
			es: "KISS1R / GPR54",
			fr: "KISS1R / GPR54",
			nl: "KISS1R / GPR54",
			pt: "KISS1R / GPR54",
			"pt-br": "KISS1R / GPR54",
			pl: "KISS1R / GPR54",
			sv: "KISS1R / GPR54",
			da: "KISS1R / GPR54",
			el: "KISS1R / GPR54",
			cs: "KISS1R / GPR54",
			fi: "KISS1R / GPR54",
			ro: "KISS1R / GPR54",
			hu: "KISS1R / GPR54",
			sk: "KISS1R / GPR54",
			tr: "KISS1R / GPR54",
			nb: "KISS1R / GPR54",
			bg: "KISS1R / GPR54",
			hr: "KISS1R / GPR54",
			sl: "KISS1R / GPR54",
			sr: "KISS1R / GPR54",
			bs: "KISS1R / GPR54",
			mk: "KISS1R / GPR54",
			lt: "KISS1R / GPR54",
			lv: "KISS1R / GPR54",
			et: "KISS1R / GPR54",
		},
		calculatorMode: "research-dilution",
		// Saleor verification 2026-09-23: one 10 mg vial per unit (1/2/3-pack variants),
		// live, purchasable, priced, and in stock in both EU and US channels.
		defaultVialSizes: [10],
		defaultVialSize: 10,
		defaultBacWater: 2,
		defaultDose: 0.1,
		halfLife: "—",
		frequency: "—",
		recommendedSyringe: 0.3,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "kisspeptin-10",
		relatedPeptides: ["pt-141", "sermorelin"],
		synonyms: ["Kisspeptin 10", "Kisspeptin10"],
	},
	{
		slug: "hgh-191aa",
		name: {
			en: "HGH 191AA",
			de: "HGH 191AA",
			it: "HGH 191AA",
			es: "HGH 191AA",
			fr: "HGH 191AA",
			nl: "HGH 191AA",
			pt: "HGH 191AA",
			"pt-br": "HGH 191AA",
			pl: "HGH 191AA",
			sv: "HGH 191AA",
			da: "HGH 191AA",
			el: "HGH 191AA",
			cs: "HGH 191AA",
			fi: "HGH 191AA",
			ro: "HGH 191AA",
			hu: "HGH 191AA",
			sk: "HGH 191AA",
			tr: "HGH 191AA",
			nb: "HGH 191AA",
			bg: "HGH 191AA",
			hr: "HGH 191AA",
			sl: "HGH 191AA",
			sr: "HGH 191AA",
			bs: "HGH 191AA",
			mk: "HGH 191AA",
			lt: "HGH 191AA",
			lv: "HGH 191AA",
			et: "HGH 191AA",
		},
		category: {
			en: "Growth Hormone",
			de: "Wachstumshormon",
			it: "Ormone della crescita",
			es: "Hormona del crecimiento",
			fr: "Hormone de croissance",
			nl: "Groeihormoon",
			pt: "Hormona do crescimento",
			"pt-br": "Hormônio do crescimento",
			pl: "Hormon wzrostu",
			sv: "Tillväxthormon",
			da: "Væksthormon",
			el: "Αυξητική ορμόνη",
			cs: "Růstový hormon",
			fi: "Kasvuhormoni",
			ro: "Hormon de creștere",
			hu: "Növekedési hormon",
			sk: "Rastový hormón",
			tr: "Büyüme hormonu",
			nb: "Veksthormon",
			bg: "Хормон на растежа",
			hr: "Hormon rasta",
			sl: "Rastni hormon",
			sr: "Hormon rasta",
			bs: "Hormon rasta",
			mk: "Хормон на раст",
			lt: "Augimo hormonas",
			lv: "Augšanas hormons",
			et: "Kasvuhormoon",
		},
		defaultVialSizes: [10],
		defaultVialSize: 10,
		defaultBacWater: 1,
		defaultDose: 2,
		doseRange: { min: 1, max: 5 },
		doseUnit: "mg",
		dosePresets: [1, 2, 3, 4, 5],
		halfLife: "",
		frequency: "once-daily",
		recommendedSyringe: 0.3,
		// Delisted: the Saleor product resolves to null in BOTH channels (verified
		// 2026-08-21), so /products/hgh-191aa is a dead end. Calculator + half-life
		// surfaces stay; no buy gate may claim it.
		hasBergdorfProduct: false,
		relatedPeptides: ["tesamorelin", "bpc-157", "tb-500"],
		cas: "12629-01-5",
		molecularFormula: "C₉₉₀H₁₅₂₉N₂₆₃O₂₉₉S₇",
		molarMass: "22 124 g/mol",
		synonyms: ["Somatropin", "rhGH", "Recombinant Human Growth Hormone"],
		studyReferences: [
			{
				author: "Molitch et al.",
				journal: "JCEM",
				year: 2011,
				title: "Evaluation and treatment of adult growth hormone deficiency",
				url: "https://pubmed.ncbi.nlm.nih.gov/21632573/",
			},
			{
				author: "Cook et al.",
				journal: "Endocr Pract",
				year: 2009,
				title: "AACE medical guidelines for growth hormone use in adults",
				url: "https://pubmed.ncbi.nlm.nih.gov/20228036/",
			},
			{
				author: "Hoffman et al.",
				journal: "Lancet",
				year: 1994,
				title: "Diagnosis of growth-hormone deficiency in adults",
				url: "https://pubmed.ncbi.nlm.nih.gov/7906812/",
			},
		] satisfies StudyReference[],
	},
	{
		slug: "nad-plus",
		name: {
			en: "NAD+",
			de: "NAD+",
			it: "NAD+",
			es: "NAD+",
			fr: "NAD+",
			nl: "NAD+",
			pt: "NAD+",
			"pt-br": "NAD+",
			pl: "NAD+",
			sv: "NAD+",
			da: "NAD+",
			el: "NAD+",
			cs: "NAD+",
			fi: "NAD+",
			ro: "NAD+",
			hu: "NAD+",
			sk: "NAD+",
			tr: "NAD+",
			nb: "NAD+",
			bg: "NAD+",
			hr: "NAD+",
			sl: "NAD+",
			sr: "NAD+",
			bs: "NAD+",
			mk: "NAD+",
			lt: "NAD+",
			lv: "NAD+",
			et: "NAD+",
		},
		category: {
			en: "Longevity / Cofactor",
			de: "Langlebigkeit / Kofaktor",
			it: "Longevità / Cofattore",
			es: "Longevidad / Cofactor",
			fr: "Longévité / Cofacteur",
			nl: "Levensduur / cofactor",
			pt: "Longevidade / Cofator",
			"pt-br": "Longevidade / Cofator",
			pl: "Długowieczność / Kofaktor",
			sv: "Livslängd / kofaktor",
			da: "Aldringsforskning / kofaktor",
			el: "Μακροζωία / Συμπαράγοντας",
			cs: "Dlouhověkost / kofaktor",
			fi: "Pitkäikäisyys / kofaktori",
			ro: "Longevitate / Cofactor",
			hu: "Hosszú élettartam / kofaktor",
			sk: "Dlhovekosť / kofaktor",
			tr: "Uzun yaşam / kofaktör",
			nb: "Levetid / kofaktor",
			bg: "Дълголетие / Кофактор",
			hr: "Dugovječnost / kofaktor",
			sl: "Dolgoživost / kofaktor",
			sr: "Dugovečnost / kofaktor",
			bs: "Dugovječnost / kofaktor",
			mk: "Долговечност / кофактор",
			lt: "Ilgaamžiškumas / kofaktorius",
			lv: "Ilmūžība / kofaktors",
			et: "Pikaajalisus / kofaktor",
		},
		defaultVialSizes: [1000],
		defaultVialSize: 1000,
		defaultBacWater: 10,
		defaultDose: 250,
		doseRange: { min: 100, max: 500 },
		doseUnit: "mg",
		dosePresets: [100, 250, 400, 500],
		// Previous value was an unreferenced estimate (source verification 2026-08-11).
		halfLife: "",
		frequency: "once-daily",
		recommendedSyringe: 1.0,
		hasBergdorfProduct: true,
		bergdorfProductSlug: "nad-plus",
		relatedPeptides: ["bpc-157", "tb-500", "tesamorelin"],
		cas: "53-84-9",
		molecularFormula: "C₂₁H₂₇N₇O₁₄P₂",
		molarMass: "663 g/mol",
		synonyms: ["beta-NAD+", "Coenzyme I", "DPN", "Nadide"],
		studyReferences: [
			{
				author: "Rajman, Chwalek, Sinclair",
				journal: "Cell Metab",
				year: 2018,
				title: "Therapeutic Potential of NAD-Boosting Molecules",
				url: "https://pubmed.ncbi.nlm.nih.gov/29514064/",
			},
			{
				author: "Yoshino, Baur, Imai",
				journal: "Cell Metab",
				year: 2018,
				title: "NAD+ Intermediates: Biology and Therapeutic Potential",
				url: "https://pubmed.ncbi.nlm.nih.gov/29249689/",
			},
			{
				author: "Brakedal et al.",
				journal: "Cell Metab",
				year: 2022,
				title: "NADPARK study: NR supplementation in Parkinson's disease",
				url: "https://pubmed.ncbi.nlm.nih.gov/35235774/",
			},
		] satisfies StudyReference[],
	},
];

/** Preferred display order only; categories absent here still appear in catalog order. */
const PEPTIDE_CATEGORY_ORDER: readonly string[] = [
	"Other",
	"GLP-1/GIP/GCG",
	"GLP-1",
	"GLP-1/GIP",
	"Amylin Analogue",
	"Neuropeptide Research",
	"Copper Peptide",
	"Tissue Repair",
	"Thymosin Beta-4",
	"GHRH Analog",
	"GHRP",
	"Fat Loss",
	"Bremelanotide",
	"Mitochondrial",
	"Anxiolytic",
	"Elamipretide",
	"Stack",
	"Melanocortin",
	"Thymic Peptide",
	"Longevity",
	"Immune / Barrier",
	"Metabolic Fragment",
	"Cognitive",
	"Sleep",
	"Metabolic",
];

/** Group every catalog peptide by its stable English category, with localized labels. */
export function getPeptideCategoryGroups(locale: Locale) {
	const groups = new Map<string, { key: string; label: string; peptides: PeptideData[] }>();
	for (const peptide of PEPTIDE_DATA) {
		if (peptide.slug === CUSTOM_PEPTIDE_SLUG) continue;
		const key = peptide.category.en;
		const group = groups.get(key);
		if (group) {
			group.peptides.push(peptide);
		} else {
			groups.set(key, { key, label: getPeptideCategory(peptide, locale), peptides: [peptide] });
		}
	}
	const preferred = PEPTIDE_CATEGORY_ORDER.flatMap((key) => {
		const group = groups.get(key);
		if (!group) return [];
		groups.delete(key);
		return [group];
	});
	return [...preferred, ...groups.values()];
}

export function getPeptideBySlug(slug: string): PeptideData | undefined {
	return PEPTIDE_DATA.find((p) => p.slug === slug);
}

/**
 * Look up a peptide by its Saleor product slug (`bergdorfProductSlug`),
 * falling back to the calculator slug. Used by the PDP route since the
 * Saleor product slug can differ from the calculator slug
 * (e.g. melanotan-2 vs mt-2).
 */
export function getPeptideByProductSlug(productSlug: string): PeptideData | undefined {
	return (
		PEPTIDE_DATA.find((p) => p.bergdorfProductSlug === productSlug) ??
		PEPTIDE_DATA.find((p) => p.slug === productSlug)
	);
}

export function getAllPeptideSlugs(): string[] {
	return PEPTIDE_DATA.map((p) => p.slug).filter((slug) => slug !== CUSTOM_PEPTIDE_SLUG);
}

/**
 * Calculator slugs that are CANDIDATES for a buy-bridge card — peptides flagged with a
 * Bergdorf product. This is only the candidate set: actual liveness + stock are re-checked
 * against the peptide-links allow-list and Saleor at request time in `getBuyBridgeProducts`
 * (so a stale `hasBergdorfProduct` flag, e.g. on a delisted peptide, never strands a card).
 * Used to batch-prefetch the hub buy-bridge map so the card follows the selected peptide.
 */
export function getBuyBridgeCandidateSlugs(): string[] {
	return PEPTIDE_DATA.filter((p) => p.hasBergdorfProduct && p.slug !== CUSTOM_PEPTIDE_SLUG).map(
		(p) => p.slug,
	);
}

/** All peptides eligible for a Half-Life Calculator spoke (numeric half-life present). */
export function getHalfLifePeptides(): PeptideData[] {
	return PEPTIDE_DATA.filter((p) => typeof p.halfLifeHours === "number" && p.halfLifeHours > 0);
}

/** Slug list for `generateStaticParams` on the half-life spoke route. */
export function getHalfLifePeptideSlugs(): string[] {
	return getHalfLifePeptides().map((p) => p.slug);
}

/** Look up a peptide by slug, but only return it if it has half-life data. */
export function getHalfLifePeptideBySlug(slug: string): PeptideData | undefined {
	const p = getPeptideBySlug(slug);
	return p && typeof p.halfLifeHours === "number" && p.halfLifeHours > 0 ? p : undefined;
}
